import axios from 'axios';

const configuredApiUrl = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || 'http://localhost:8003';
const API_URL = (() => {
  if (typeof window === 'undefined') return configuredApiUrl;
  const host = window.location.hostname;
  const isLocalPage = host === 'localhost' || host === '127.0.0.1';
  const isLocalApi = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?/i.test(configuredApiUrl);
  if (isLocalPage && isLocalApi) {
    return `${window.location.protocol}//${host}:8003`;
  }
  return configuredApiUrl;
})();

const api = axios.create({
  baseURL: `${API_URL}/api/v1`,

});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// Global (federated) search — GET /search?q=&limit= (Phase 1 backend).
export const searchAPI = {
  global: (q = '', limit = 5) => api.get('/search', { params: { q, limit } }).then((r) => r.data),
};

// Daily activity logs and driver assignments APIs
export const dailyActivityAPI = {
  // Daily activity logs
  createDailyLog: (data) => api.post('/daily-activity/logs/create', data).then(res => res.data),
  getDailyLog: (id) => api.get(`/daily-activity/logs/${id}`).then(res => res.data),
  listLogsForAssignment: (assignmentId) => api.get(`/daily-activity/logs/assignment/${assignmentId}`).then(res => res.data),
  listLogsForDate: (date) => api.get(`/daily-activity/logs/date/${date}`).then(res => res.data),
  listLogsForDriverCampaign: (campaignId, driverId) => api.get(`/daily-activity/logs/campaign/${campaignId}/driver/${driverId}`).then(res => res.data),
  updateDailyLog: (id, data) => api.put(`/daily-activity/logs/${id}`, data).then(res => res.data),
  deleteDailyLog: (id) => api.delete(`/daily-activity/logs/${id}`).then(res => res.data),
  addVillageToLog: (logId, villageName) => api.post(`/daily-activity/logs/${logId}/add-village?village_name=${encodeURIComponent(villageName)}`).then(res => res.data),
  addImageToLog: (logId, imageUrl) => api.post(`/daily-activity/logs/${logId}/add-image?image_url=${encodeURIComponent(imageUrl)}`).then(res => res.data),

  // Activity counts
  getActivityCountForAssignment: (assignmentId) => api.get(`/daily-activity/count/assignment/${assignmentId}`).then(res => res.data),
  getActivityCountForDriverInCampaign: (campaignId, driverId) => api.get(`/daily-activity/count/campaign/${campaignId}/driver/${driverId}`).then(res => res.data),
  getDailyActivityCount: (date) => api.get(`/daily-activity/count/date/${date}`).then(res => res.data),

  // Driver assignments
  listAssignmentsForDriver: (driverId) => api.get(`/daily-activity/assignments/driver/${driverId}`).then(res => res.data),
  listAssignmentsForCampaign: (campaignId) => api.get(`/daily-activity/assignments/campaign/${campaignId}`).then(res => res.data),
  getByDriver: (driverId) => api.get(`/daily-activity/logs/driver/${driverId}`).then(res => ({
    activities: res.data || []
  })).catch(() => ({ activities: [] })),
};

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  // Google SSO — public button config + ID-token exchange (see components/auth/GoogleSignInButton)
  googleSsoConfig: () => api.get('/auth/google/config').then((r) => r.data),
  googleLogin: (data) => api.post('/auth/google', data),
  sendDriverOtp: (data) => api.post('/auth/driver/send-otp', data),
  verifyDriverOtp: (data) => api.post('/auth/driver/verify-otp', data),
  sendGodownManagerOtp: (data) => api.post('/auth/godown-manager/send-otp', data),
  verifyGodownManagerOtp: (data) => api.post('/auth/godown-manager/verify-otp', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data).then((r) => r.data),
  impersonate: (userId) => api.post(`/auth/impersonate/${userId}`).then((r) => r.data),
};

export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

export const clientsAPI = {
  getAll: (params) => api.get('/clients', { params }).then((res) => ({ ...res, data: res.data.items ?? res.data })),
  getList: (params) => api.get('/clients', { params }),
  getOne: (id) => api.get(`/clients/${id}`),
  getExpenses: (id) => api.get(`/clients/${id}/expenses`),
  getFinancialSummary: (id) => api.get(`/clients/${id}/financial-summary`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  getDeletionPreview: (id) => api.get(`/clients/${id}/deletion-preview`),
};

const getAllPaginated = async (path, params = {}) => {
  const rest = { ...(params || {}) };
  delete rest.page;
  let page = 1;
  let items = [];

  while (true) {
    const response = await api.get(path, {
      params: {
        ...rest,
        page,
      },
    });

    const payload = response.data;
    const pageItems = payload?.items ?? payload;

    if (!Array.isArray(pageItems)) {
      return response;
    }

    items = items.concat(pageItems);
    const meta = payload?.meta;
    const totalPages = Number(meta?.total_pages || 1);

    if (!meta || page >= totalPages || pageItems.length === 0) {
      return { ...response, data: items };
    }

    page += 1;
  }
};

export const projectsAPI = {
  getAll: (params) => getAllPaginated('/projects', params),
  getList: (params) => api.get('/projects', { params }),
  getOne: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),

  // Internal team members on the project (CS lead / designer / writer / PM /
  // any internal user). Different from campaign team allocations which carry
  // vendor/driver/promoter/anchor for on-ground execution.
  listTeam: (projectId) =>
    api.get(`/projects/${projectId}/team`).then((r) => r.data),
  addTeamMember: (projectId, payload) =>
    api.post(`/projects/${projectId}/team`, payload).then((r) => r.data),
  updateTeamMember: (projectId, memberId, payload) =>
    api.patch(`/projects/${projectId}/team/${memberId}`, payload).then((r) => r.data),
  removeTeamMember: (projectId, memberId) =>
    api.delete(`/projects/${projectId}/team/${memberId}`),
};

export const campaignsAPI = {
  getAll: (params) => getAllPaginated('/campaigns', params),
  getList: (params) => api.get('/campaigns', { params }),
  getOne: (id) => api.get(`/campaigns/${id}`),
  getById: (id) => api.get(`/campaigns/${id}`),
  getForDriver: (driverId) => api.get(`/daily-activity/assignments/driver/${driverId}`).then(res => res.data.assignments?.map(a => ({ id: a.campaign_id, name: a.campaign_name, ...a })) || []),
  create: (data) => api.post('/campaigns', data),
  update: (id, data) => api.put(`/campaigns/${id}`, data),
  delete: (id) => api.delete(`/campaigns/${id}`),

  // Internal team members on the campaign — mirrors projectsAPI.list/addTeam
  // but per-campaign. Used for campaign-execution crew (campaign coordinator,
  // dedicated designer, content writer) that's distinct from project-level
  // staff. Field crew (vendor/driver/promoter/anchor) lives in Team
  // Allocations, separate from this list.
  listTeam: (campaignId) =>
    api.get(`/campaigns/${campaignId}/team`).then((r) => r.data),
  addTeamMember: (campaignId, payload) =>
    api.post(`/campaigns/${campaignId}/team`, payload).then((r) => r.data),
  updateTeamMember: (campaignId, memberId, payload) =>
    api.patch(`/campaigns/${campaignId}/team/${memberId}`, payload).then((r) => r.data),
  removeTeamMember: (campaignId, memberId) =>
    api.delete(`/campaigns/${campaignId}/team/${memberId}`),
};

const uploadReferenceFile = (url, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then((res) => res.data);
};

export const referenceAttachmentsAPI = {
  listProject: (projectId) => api.get(`/projects/${projectId}/attachments`).then((res) => res.data),
  uploadProject: (projectId, file) => uploadReferenceFile(`/projects/${projectId}/attachments/upload`, file),
  deleteProject: (projectId, attachmentId) => api.delete(`/projects/${projectId}/attachments/${attachmentId}`),
  listCampaign: (campaignId) => api.get(`/campaigns/${campaignId}/attachments`).then((res) => res.data),
  uploadCampaign: (campaignId, file) => uploadReferenceFile(`/campaigns/${campaignId}/attachments/upload`, file),
  deleteCampaign: (campaignId, attachmentId) => api.delete(`/campaigns/${campaignId}/attachments/${attachmentId}`),
  listPurchaseRequest: (requestId) => api.get(`/purchase/requests/${requestId}/attachments`).then((res) => res.data),
  uploadPurchaseRequest: (requestId, file) => uploadReferenceFile(`/purchase/requests/${requestId}/attachments/upload`, file),
  deletePurchaseRequest: (requestId, attachmentId) => api.delete(`/purchase/requests/${requestId}/attachments/${attachmentId}`),
};

export const campaignLocationsAPI = {
  getByCampaign: (campaignId) => api.get(`/campaign-locations/campaign/${campaignId}`),
  getOne: (id) => api.get(`/campaign-locations/${id}`),
  create: (data) => api.post('/campaign-locations', data),
  bulkUpload: (campaignId, formData) =>
    api.post(`/campaign-locations/campaign/${campaignId}/bulk-upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  update: (id, data) => api.put(`/campaign-locations/${id}`, data),
  delete: (id) => api.delete(`/campaign-locations/${id}`),
};

export const teamAllocationsAPI = {
  getByCampaign: (campaignId) => api.get(`/team-allocations/campaign/${campaignId}`),
  getByCampaignDetailed: (campaignId) => api.get(`/team-allocations/campaign/${campaignId}/detailed`),
  getByDriverDetailed: (driverId) => api.get(`/team-allocations/driver/${driverId}/detailed`),
  getByPromoterDetailed: (promoterId) => api.get(`/team-allocations/promoter/${promoterId}/detailed`),
  getByAnchorDetailed: (anchorUserId) => api.get(`/team-allocations/anchor/${anchorUserId}/detailed`),
  getByLocation: (campaignLocationId) => api.get(`/team-allocations/location/${campaignLocationId}`),
  getOne: (id) => api.get(`/team-allocations/${id}`),
  create: (data) => api.post('/team-allocations', data),
  bulkUpload: (campaignId, formData) =>
    api.post(`/team-allocations/campaign/${campaignId}/bulk-upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  update: (id, data) => api.put(`/team-allocations/${id}`, data),
  delete: (id) => api.delete(`/team-allocations/${id}`),
};

export const reportTemplatesAPI = {
  getAll: (params) => api.get('/report-templates', { params }),
  getOne: (id) => api.get(`/report-templates/${id}`),
  getActive: (campaignId, campaignLocationId) =>
    api.get('/report-templates/active', {
      params: {
        campaign_id: campaignId,
        campaign_location_id: campaignLocationId,
      },
    }),
  create: (data) => api.post('/report-templates', data),
  update: (id, data) => api.put(`/report-templates/${id}`, data),
  uploadSource: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/report-templates/${id}/source/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  assign: (data) => api.post('/report-templates/assignments', data),
};

const normaliseMobileReportField = (field = {}, index = 0) => ({
  ...field,
  id: field.id || field.key || field.field_key || `mobile-field-${index}`,
  field_key: field.field_key || field.key || '',
  field_label: field.field_label || field.label || field.field_key || field.key || '',
  field_type: field.field_type || field.type || 'text',
  is_required: typeof field.is_required === 'boolean' ? field.is_required : Boolean(field.required),
  is_readonly: typeof field.is_readonly === 'boolean' ? field.is_readonly : Boolean(field.readonly),
  is_system: typeof field.is_system === 'boolean' ? field.is_system : Boolean(field.system),
  system_field_key: field.system_field_key || field.system_key || null,
  has_image_attachment:
    typeof field.has_image_attachment === 'boolean'
      ? field.has_image_attachment
      : Boolean(field.has_attachment),
  sort_order: field.sort_order ?? field.order ?? index,
  help_text: field.help_text || null,
  placeholder: field.placeholder || '',
  options: Array.isArray(field.options) ? field.options : [],
  visibility_roles: Array.isArray(field.visibility_roles) ? field.visibility_roles : [],
  is_repeatable: Boolean(field.is_repeatable),
});

const normaliseMobileReportForm = (form = {}) => ({
  ...form,
  team_allocation_id: form.team_allocation_id || form.meta?.team_allocation_id || null,
  report_date: form.report_date || form.meta?.report_date || null,
  report_source: form.report_source || form.meta?.report_source || null,
  source_name: form.source_name || form.meta?.source_name || null,
  report_id: form.report_id ?? form.meta?.report_id ?? null,
  report_status: form.report_status ?? form.meta?.report_status ?? null,
  template_id: form.template_id ?? form.meta?.template?.id ?? null,
  template_version: form.template_version ?? form.meta?.template?.version ?? null,
  campaign_id: form.campaign_id || form.campaign?.id || null,
  campaign_name: form.campaign_name || form.campaign?.name || null,
  campaign_location_id: form.campaign_location_id || form.campaign?.location?.id || null,
  campaign_location_name: form.campaign_location_name || form.campaign?.location?.name || null,
  project_id: form.project_id || form.project?.id || null,
  project_name: form.project_name || form.project?.name || null,
  effective_roles: Array.isArray(form.effective_roles) ? form.effective_roles : (form.user_context?.roles || []),
  values: form.values || form.form_state?.values || {},
  activity_slots: form.activity_slots || form.form_state?.activity_slots || [],
  attachments: Array.isArray(form.attachments) ? form.attachments : [],
  remarks: form.remarks ?? form.form_state?.remarks ?? null,
  sections: Array.isArray(form.sections)
    ? form.sections.map((section) => ({
      ...section,
      id: section.id || section.section_key || section.key || null,
      section_key: section.section_key || section.key || '',
      sort_order: section.sort_order ?? section.order ?? 0,
      fields: Array.isArray(section.fields)
        ? section.fields.map((field, index) => normaliseMobileReportField(field, index))
        : [],
    }))
    : [],
});

export const mobileReportsAPI = {
  getMyCampaigns: (reportDate, includeAll = false) =>
    api.get('/mobile/my-campaigns', {
      params: {
        report_date: reportDate,
        include_all: includeAll || undefined,
      },
    }),
  getForm: (teamAllocationId, reportDate) =>
    api.get('/mobile/report-form', {
      params: {
        team_allocation_id: teamAllocationId,
        report_date: reportDate,
      },
    }).then((response) => ({
      ...response,
      data: normaliseMobileReportForm(response.data),
    })),
  getTodayContext: (reportDate) =>
    api.get('/mobile/reports/today', { params: { report_date: reportDate } }),
  getOfficeReports: (params = {}) =>
    api.get('/mobile/reports/office', { params }).then((res) => ({ ...res, data: res.data.items ?? res.data })),
  getOfficeReportsList: (params = {}) =>
    api.get('/mobile/reports/office', { params }),
  updateOfficeReport: (id, data) =>
    api.patch(`/mobile/reports/office/${id}`, data),
  deleteOfficeReports: (params = {}) =>
    api.delete('/mobile/reports/office', { params }),
  getHistory: (limit = 30) =>
    api.get('/mobile/reports/history', { params: { limit } }),
  getOne: (id) => api.get(`/mobile/reports/${id}`),
  saveDraft: (data) => api.post('/mobile/reports/draft', data),
  submit: (id) => api.post(`/mobile/reports/${id}/submit`),
  review: (id, data) => api.post(`/mobile/reports/${id}/review`, data),
  uploadAttachment: (reportId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/daily-reports/${reportId}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((res) => res.data);
  },
  deleteAttachment: (reportId, attachmentId) =>
    api.delete(`/daily-reports/${reportId}/attachments/${attachmentId}`),
};

export const voiceAgentAPI = {
  createRealtimeSession: (data) => api.post('/voice-agent/realtime-session', data),
  createWebrtcOffer: (sdp, context = null) => {
    if (!context) {
      return api.post('/voice-agent/webrtc-offer', sdp, {
        headers: { 'Content-Type': 'application/sdp' },
        transformRequest: [(data) => data],
        responseType: 'text',
      });
    }
    return api.post('/voice-agent/webrtc-offer', { sdp, context }, {
      responseType: 'text',
    });
  },
  createExotelOutboundCall: (data) => api.post('/voice-agent/exotel/outbound-call', data),
};

export const driverFormsAPI = {
  getProjectForm: (projectId) => api.get(`/driver/projects/${projectId}/form`),
};

export const vendorsAPI = {
  getAll: (params) => api.get('/vendors', { params }).then((res) => ({ ...res, data: res.data.items ?? res.data })),
  getList: (params) => api.get('/vendors', { params }),
  // Fetch EVERY vendor across all pages (the API caps page_size at 500, so a
  // single request silently drops vendors past the first 500). Use this for
  // vendor pickers (PO creation, RFQ) so client-side search sees all vendors.
  getAllForSelect: (params) => getAllPaginated('/vendors', { page_size: 500, ...(params || {}) }),
  getOne: (id) => api.get(`/vendors/${id}`),
  create: (data) => api.post('/vendors', data),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  delete: (id) => api.delete(`/vendors/${id}`),
  // Save a vendor's bank details (Accounts only).
  saveBankDetails: (id, data) => api.put(`/vendors/${id}/bank-details`, data).then((res) => res.data),
  // Mark a vendor as one we never raise a PO against — ad platforms, utilities,
  // statutory payments. Stops their bills reading as PO discrepancies. Accounts
  // only (finance.update); changes no money.
  setPORequirement: (id, { po_not_required, reason }) =>
    api.patch(`/vendors/${id}/po-requirement`, { po_not_required, reason }).then((res) => res.data),
  // Bulk vendor bank details (Accounts): download a template for a set of
  // vendors (or all missing-bank vendors), then upload → preview → apply.
  bankTemplate: async ({ vendorIds = null, missingOnly = false } = {}) => {
    const res = await api.post(
      '/vendors/bank-details/template',
      { vendor_ids: vendorIds, missing_only: missingOnly },
      { responseType: 'blob' },
    );
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'vendor_bank_details_template.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
  bankBulkPreview: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/vendors/bank-details/bulk-preview', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((res) => res.data);
  },
  bankBulkApply: (items) =>
    api.post('/vendors/bank-details/bulk-apply', { items }).then((res) => res.data),
};

// The bank fields a vendor must have before a payment request can be raised.
export const vendorHasBankDetails = (vendor) =>
  !!(vendor && String(vendor.bank_account_holder || '').trim() && String(vendor.bank_name || '').trim()
    && String(vendor.bank_account_number || '').trim() && String(vendor.bank_ifsc || '').trim());

export const vehiclesAPI = {
  getAll: () => api.get('/vehicles'),
  getOne: (id) => api.get(`/vehicles/${id}`),
  getByVendor: (vendorId) => api.get('/vehicles', { params: { vendor_id: vendorId } }).then(res => ({
    vehicles: res.data.filter(v => v.vendor_id == vendorId)
  })),
  create: (data) => api.post('/vehicles', data),
  update: (id, data) => api.put(`/vehicles/${id}`, data),
  delete: (id) => api.delete(`/vehicles/${id}`),
  toggleStatus: (id, data) => api.patch(`/vehicles/${id}/toggle-status`, data),
};

export const driversAPI = {
  getAll: () => api.get('/drivers'),
  getOne: (id) => api.get(`/drivers/${id}`),
  // All uploaded documents for a driver (view/download on Driver Details).
  getDocuments: (id) => api.get(`/drivers/${id}/documents`).then((res) => res.data),
  create: (data) => api.post('/drivers', data),
  update: (id, data) => api.put(`/drivers/${id}`, data),
  delete: (id) => api.delete(`/drivers/${id}`),
  uploadLicense: (driverId, formData) =>
    api.post(`/drivers/${driverId}/upload-license`, formData),
  uploadDocument: (driverId, formData) =>
    api.post(`/drivers/${driverId}/upload-document`, formData),
  uploadAdvanceProof: (driverId, formData) =>
    api.post(`/drivers/${driverId}/upload-advance-proof`, formData),
  bulkSendWhatsApp: (data) => api.post('/drivers/whatsapp/bulk-send', data),
  toggleStatus: (id, data) => api.patch(`/drivers/${id}/toggle-status`, data),
};

export const uploadAPI = {
  vehicleDocument: (formData) => api.post('/upload/vehicle-document', formData),
};

export const promotersAPI = {
  getAll: () => api.get('/promoters'),
  getOne: (id) => api.get(`/promoters/${id}`),
  create: (data) => api.post('/promoters', data),
  update: (id, data) => api.patch(`/promoters/${id}`, data),
  delete: (id) => api.delete(`/promoters/${id}`),
};

export const anchorsAPI = {
  getAll: () => api.get('/anchors'),
  getOne: (id) => api.get(`/anchors/${id}`),
  create: (data) => api.post('/anchors', data),
  update: (id, data) => api.patch(`/anchors/${id}`, data),
  delete: (id) => api.delete(`/anchors/${id}`),
};

export const feedbackAPI = {
  list: (params) => api.get('/feedback', { params }).then((res) => res.data),
  getOne: (id) => api.get(`/feedback/${id}`).then((res) => res.data),
  create: (data) => api.post('/feedback', data).then((res) => res.data),
  update: (id, data) => api.patch(`/feedback/${id}`, data).then((res) => res.data),
};

export const promoterActivitiesAPI = {
  getAll: (params) => api.get('/promoter-activities', { params }),
  getOne: (id) => api.get(`/promoter-activities/${id}`),
  getStats: (campaignId) => api.get('/promoter-activities/stats', { params: { campaign_id: campaignId } }),
  create: (data) => api.post('/promoter-activities', data),
  update: (id, data) => api.put(`/promoter-activities/${id}`, data),
  delete: (id) => api.delete(`/promoter-activities/${id}`),
  uploadImage: (id, imageType, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('image_type', imageType);
    return api.post(`/promoter-activities/${id}/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadVideo: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/promoter-activities/${id}/upload-video`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

export const expensesAPI = {
  getAll: (params) => api.get('/expenses', { params }),
  getOne: (id) => api.get(`/expenses/${id}`),
  // Auto-fill the Vendor Expense form's invoice fields from the matching
  // Tally purchase voucher. Returns { match: null } when nothing matches.
  suggestVendorInvoice: (params) =>
    api.get('/expenses/suggest-vendor-invoice', { params }).then((r) => r.data),
  // Pay many approved vendor bills in one bank run. payments: [{expense_id,
  // paid_amount, transaction_id, payment_method, remarks}]. Returns per-row result.
  payBulk: (payments) => api.post('/expenses/pay-bulk', { payments }).then((r) => r.data),
  // Approved vendor bills with no payment yet — worklist for bulk pay.
  payableVendorBills: () => api.get('/expenses/payable-vendor-bills').then((r) => r.data),
  create: (data) => {
    // Check if data is FormData (for image upload)
    if (data instanceof FormData) {
      return api.post('/expenses', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return api.post('/expenses', data);
  },
  approve: (id, data) => api.patch(`/expenses/${id}/approve`, data),
  reject: (id, data) => api.patch(`/expenses/${id}/reject`, data),
  getDriverApprovals: () => api.get('/expenses/driver/pending-approvals').then((res) => res.data),
  getAssignedApprovals: () => api.get('/expenses/assigned/pending-approvals').then((res) => res.data),
  getPaymentReviewQueue: () => api.get('/expenses/assigned/payment-review').then((res) => res.data),
  getApprovalDailyReports: (id) => api.get(`/expenses/${id}/daily-reports`).then((res) => res.data),
  getMyAccount: () => api.get('/expenses/my-account'),
  getDriverAccount: (driverId) => api.get(`/expenses/driver-account/${driverId}`),
  getPromoterAccount: (promoterId) => api.get(`/expenses/promoter-account/${promoterId}`),
  getAnchorAccount: (anchorId) => api.get(`/expenses/anchor-account/${anchorId}`),
  update: (id, data) => {
    // Check if data is FormData (for image upload)
    if (data instanceof FormData) {
      return api.put(`/expenses/${id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return api.put(`/expenses/${id}`, data);
  },
  delete: (id) => api.delete(`/expenses/${id}`),
  // Reverse an approved/paid expense — creates a paired -amount entry,
  // both flip to 'reversed' once the reversal is approved.
  reverse: (id, reason) => api.post(`/expenses/${id}/reverse`, { reason }),
  // Audit-log feed for the detail page tab — every correction.
  auditLog: (id) => api.get(`/expenses/${id}/audit-log`).then((r) => r.data),
  // Bulk upload — multipart .xlsx; backend returns batch_id + summary.
  bulkUpload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/expenses/bulk-upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },
  // Returns the .xlsx template as a blob — UI triggers download.
  bulkUploadTemplate: () =>
    api.get('/expenses/bulk-upload/template.xlsx', { responseType: 'blob' }),
  // Reconciliation queue — duplicates flagged by the bulk-upload dedupe job.
  reconciliationQueue: () => api.get('/expenses/reconciliation').then((r) => r.data.pairs),
  resolveReconciliation: (suspectId, action, reason) =>
    api.post(`/expenses/reconciliation/${suspectId}/resolve`, { action, reason }).then((r) => r.data),
};

export const advancesAPI = {
  getAll: (params) => api.get('/advances', { params }),
  create: (formData) => api.post('/advances', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const reportsAPI = {
  getAll: () => api.get('/reports'),
  getOne: (id) => api.get(`/reports/${id}`),
  getByCampaign: (id) => api.get(`/reports/campaign/${id}`),
  create: (data) => api.post('/reports', data),
  update: (id, data) => api.put(`/reports/${id}`, data),
  delete: (id) => api.delete(`/reports/${id}`),
};

export const vendorDashboardAPI = {
  getDashboard: (vendorId) => api.get('/vendor-dashboard', { params: { vendor_id: vendorId } }),
  getMenuCounts: () => api.get('/vendor-dashboard/menu-counts').then(res => res.data),
};

export const invoicesAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  getOne: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  uploadFile: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/invoices/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (id) => api.delete(`/invoices/${id}`),
  approve: (id) => api.post(`/invoices/${id}/approve`),
  markAsPaid: (id, paymentMethod) => api.post(`/invoices/${id}/mark_paid`, { payment_method: paymentMethod }),
};

export const paymentsAPI = {
  getAll: (params) => api.get('/payments', { params }),
  getOne: (id) => api.get(`/payments/${id}`),
  create: (data) => api.post('/payments', data),
  update: (id, data) => api.put(`/payments/${id}`, data),
  delete: (id) => api.delete(`/payments/${id}`),
};

export const paymentRequestsAPI = {
  getAll: (params) => api.get('/payment-requests', { params }).then(res => res.data),
  getMine: (params) => api.get('/payment-requests/mine', { params }).then(res => res.data),
  getOne: (id) => api.get(`/payment-requests/${id}`).then(res => res.data),
  prefill: (campaignId) => api.get('/payment-requests/prefill', { params: { campaign_id: campaignId } }).then(res => res.data),
  create: (data) => api.post('/payment-requests', data).then(res => res.data),
  markLinePaid: (requestId, lineId, data) => api.patch(`/payment-requests/${requestId}/lines/${lineId}/pay`, data).then(res => res.data),
  // Reject a whole request (all pending lines) — mandatory reason + optional file.
  reject: (requestId, { reason, file } = {}) => {
    const fd = new FormData();
    fd.append('reason', reason ?? '');
    if (file) fd.append('file', file);
    return api.patch(`/payment-requests/${requestId}/reject`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(res => res.data);
  },
  // Partial rejection — reject a single driver's line.
  rejectLine: (requestId, lineId, { reason, file } = {}) => {
    const fd = new FormData();
    fd.append('reason', reason ?? '');
    if (file) fd.append('file', file);
    return api.patch(`/payment-requests/${requestId}/lines/${lineId}/reject`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(res => res.data);
  },
  // Bulk reject several whole requests at once (one shared reason + optional file).
  rejectBulk: (requestIds, { reason, file } = {}) => {
    const fd = new FormData();
    fd.append('request_ids', (requestIds || []).join(','));
    fd.append('reason', reason ?? '');
    if (file) fd.append('file', file);
    return api.post('/payment-requests/reject-bulk', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(res => res.data);
  },
  // Requester correction + resubmit of a rejected line.
  updateLine: (requestId, lineId, data) => api.patch(`/payment-requests/${requestId}/lines/${lineId}`, data).then(res => res.data),
  resubmitLine: (requestId, lineId) => api.patch(`/payment-requests/${requestId}/lines/${lineId}/resubmit`, {}).then(res => res.data),
  // Soft-delete a rejected request (leaves the active queue; kept in History).
  softDelete: (requestId) => api.patch(`/payment-requests/${requestId}/soft-delete`, {}).then(res => res.data),
  download: (requestId) => api.get(`/payment-requests/${requestId}/download`, { responseType: 'blob' }),
  downloadMerged: (requestIds) => api.post('/payment-requests/download-merged', { request_ids: requestIds }, { responseType: 'blob' }),
  getExports: () => api.get('/payment-requests/exports').then(res => res.data),
  previewExport: (batchId) => api.get(`/payment-requests/exports/${batchId}/preview`).then(res => res.data),
  downloadExport: (batchId) => api.get(`/payment-requests/exports/${batchId}/download`, { responseType: 'blob' }),
  reconcilePreview: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/payment-requests/reconcile/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(res => res.data);
  },
  reconcileApply: (items, uploadId) => api.post('/payment-requests/reconcile/apply', { items, upload_id: uploadId }).then(res => res.data),
  downloadTemplate: () => api.get('/payment-requests/reconcile/template', { responseType: 'blob' }),
  reconcileHistory: () => api.get('/payment-requests/reconcile/history').then(res => res.data),
  reconcileHistoryDetail: (id) => api.get(`/payment-requests/reconcile/history/${id}`).then(res => res.data),
  downloadReconcileFile: (id) => api.get(`/payment-requests/reconcile/history/${id}/file`, { responseType: 'blob' }),
  downloadReconcileErrors: (id) => api.get(`/payment-requests/reconcile/history/${id}/errors`, { responseType: 'blob' }),
};

export const clientServicingDashboardAPI = {
  getProjectProgress: (params) => api.get('/client-servicing-dashboard/project-progress', { params }).then(res => res.data),
  getVehicleMovement: (params) => api.get('/client-servicing-dashboard/vehicle-movement', { params }).then(res => res.data),
  getExpenseSnapshot: (params) => api.get('/client-servicing-dashboard/expense-snapshot', { params }).then(res => res.data),
  getLiveUpdates: (params) => api.get('/client-servicing-dashboard/live-updates', { params }).then(res => res.data),
  exportDashboard: (format, params) => api.get('/client-servicing-dashboard/export', {
    params: { ...params, format },
    responseType: 'blob'
  }).then(res => res.data),
};

// Driver Dashboard API
export const driverDashboardAPI = {
  getMyDashboard: (targetDate) => api.get('/driver-dashboard/me', { params: { target_date: targetDate } }).then(res => res.data),
  getProfile: () => api.get('/driver-dashboard/profile').then(res => res.data),
  updateProfile: (data) => api.put('/driver-dashboard/profile', data).then(res => res.data),
  getAssignedWork: (targetDate) => api.get('/driver-dashboard/assigned-work', { params: { target_date: targetDate } }).then(res => res.data),
  getVehicle: () => api.get('/driver-dashboard/vehicle').then(res => res.data),
  getTodayKMLog: () => api.get('/driver-dashboard/km-log/today').then(res => res.data),
  recordStartKM: (data) => {
    // Send JSON payload with base64 encoded photo
    const payload = {
      latitude: data.latitude,
      longitude: data.longitude,
      start_km_photo: data.start_km_photo // Base64 string
    };
    return api.post('/driver-dashboard/km-log/start', payload).then(res => res.data);
  },
  recordEndKM: (data) => {
    // Send JSON payload with base64 encoded photo
    const payload = {
      latitude: data.latitude,
      longitude: data.longitude,
      end_km_photo: data.end_km_photo // Base64 string
    };
    return api.post('/driver-dashboard/km-log/end', payload).then(res => res.data);
  },
  getDailySummary: (date) => api.get(`/driver-dashboard/summary/${date}`).then(res => res.data),
  getAllDriversSummary: (targetDate) => api.get('/driver-dashboard/all-summary', { params: { target_date: targetDate } }).then(res => res.data),
  getDriverDashboard: (driverId, targetDate) => api.get(`/driver-dashboard/driver/${driverId}`, { params: { target_date: targetDate } }).then(res => res.data),
  // Get journey details - works for both driver (own) and admin (any driver)
  getJourneyDetails: (driverId, date) => {
    if (driverId && driverId !== 'me') {
      // Admin/Ops viewing specific driver's journey - use full dashboard data
      return api.get(`/driver-dashboard/driver/${driverId}`, { params: { target_date: date } }).then(res => res.data);
    } else {
      // Driver viewing their own journey - use full dashboard data
      return api.get(`/driver-dashboard/me`, { params: { target_date: date } }).then(res => res.data);
    }
  },
};
// Vendor Booking API
export const vendorBookingAPI = {
  // Get vendor resources
  getCampaigns: (vendorId) => {
    const params = {};
    if (vendorId) params.vendor_id = vendorId;
    return api.get('/vendor-booking/campaigns', { params }).then(res => res.data);
  },

  getDrivers: (vendorId, activeOnly = true) => {
    const params = { active_only: activeOnly };
    if (vendorId) params.vendor_id = vendorId;
    return api.get('/vendor-booking/drivers', { params }).then(res => res.data);
  },

  getVehicles: (vendorId, availableOnly = true) => {
    const params = { available_only: availableOnly };
    if (vendorId) params.vendor_id = vendorId;
    return api.get('/vendor-booking/vehicles', { params }).then(res => res.data);
  },

  // Work assignments
  getAssignments: (vendorId, campaignId, assignmentDate) => {
    const params = {};
    if (vendorId) params.vendor_id = vendorId;
    if (campaignId) params.campaign_id = campaignId;
    if (assignmentDate) params.assignment_date = assignmentDate;
    return api.get('/vendor-booking/assignments', { params }).then(res => res.data);
  },

  getAssignmentDetails: (assignmentId) => api.get(`/vendor-booking/assignments/${assignmentId}`).then(res => res.data),

  createAssignment: (data, vendorId) => {
    const params = {};
    if (vendorId) params.vendor_id = vendorId;
    return api.post('/vendor-booking/assignments', data, { params }).then(res => res.data);
  },

  updateAssignment: (assignmentId, data, vendorId) => {
    const params = {};
    if (vendorId) params.vendor_id = vendorId;
    return api.put(`/vendor-booking/assignments/${assignmentId}`, data, { params }).then(res => res.data);
  },

  cancelAssignment: (assignmentId, remarks, vendorId) => {
    const params = { remarks };
    if (vendorId) params.vendor_id = vendorId;
    return api.post(`/vendor-booking/assignments/${assignmentId}/cancel`, null, { params }).then(res => res.data);
  },

  // Driver approval actions
  driverApproveAssignment: (assignmentId) => {
    return api.post(`/vendor-booking/assignments/${assignmentId}/driver-action`, {
      action: 'approve'
    }).then(res => res.data);
  },

  driverRejectAssignment: (assignmentId, rejectionReason) => {
    return api.post(`/vendor-booking/assignments/${assignmentId}/driver-action`, {
      action: 'reject',
      rejection_reason: rejectionReason
    }).then(res => res.data);
  },
};

// Accounts & Payments API (Admin-only)
export const accountsAPI = {
  getSummary: (fromDate, toDate, opts = {}) => {
    const params = {};
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    if (opts.allVendors) params.all_vendors = true;
    return api.get('/accounts/summary', { params });
  },
  getMetrics: () => api.get('/accounts/metrics'),
  // Email the Accounts & Payments report as a PDF. The server rebuilds the
  // report from these filters — nothing computed in the browser is trusted.
  emailReport: (payload) => api.post('/accounts/report/email', payload).then((r) => r.data),
};

// "Can I send mail as myself?" — the signed-in user's own gmail.send grant.
// Separate from purchaseAPI.gmailStatus, which reports the most recently
// connected mailbox whoever it belongs to; these two endpoints are scoped to
// the caller and are what every send-as-me flow should ask.
export const gmailMeAPI = {
  status: () => api.get('/gmail/me/status').then((r) => r.data),
  // Returns the Google consent URL for the popup to open — same hop as the
  // purchase flow, because window.open can't carry our bearer token.
  authorizeUrl: () => api.get('/gmail/me/authorize-url').then((r) => r.data),
};

// Advance reconciliation — /accounts/advances page + reconcile drawer.
export const advanceReconciliationAPI = {
  listOpen: (params = {}) => api.get('/advances/open', { params }).then((r) => r.data),
  getDetail: (id) => api.get(`/advances/${id}/detail`).then((r) => r.data),
  getBalance: (id) => api.get(`/advances/${id}/balance`).then((r) => r.data),
  listReconciliations: (id) => api.get(`/advances/${id}/reconciliations`).then((r) => r.data),
  listReconcilableExpenses: (id) => api.get(`/advances/${id}/reconcilable-expenses`).then((r) => r.data),
  reconcile: (id, body) => api.post(`/advances/${id}/reconcile`, body).then((r) => r.data),
  close: (id, body) => api.post(`/advances/${id}/close`, body).then((r) => r.data),
  reverseReconciliation: (id) => api.delete(`/advance-reconciliations/${id}`),
  bulkUpload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/advances/bulk-upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },
  bulkUploadTemplate: () =>
    api.get('/advances/bulk-upload/template.xlsx', { responseType: 'blob' }).then((r) => r.data),
};

// Audit Log — superadmin-only cross-entity event stream.
// Backend gate: users.is_superadmin = 1 (separate from role).
// Returns 403 for anyone else.
export const auditLogAPI = {
  list: (params = {}) => {
    const qs = {};
    if (params.entity_type) qs.entity_type = params.entity_type;
    if (params.entity_id) qs.entity_id = params.entity_id;
    if (params.actor_user_id) qs.actor_user_id = params.actor_user_id;
    if (params.impersonator_id) qs.impersonator_id = params.impersonator_id;
    if (params.action) qs.action = params.action;
    if (params.from) qs.from = params.from;
    if (params.to) qs.to = params.to;
    qs.limit = params.limit || 200;
    return api.get('/audit-log', { params: qs }).then((r) => r.data);
  },
};

// Operations API (Admin/Operations Manager)
export const operationsAPI = {
  getSummary: (fromDate, toDate) => {
    const params = {};
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    return api.get('/operations/summary', { params });
  },
  getMetrics: () => api.get('/operations/metrics'),
};

// Users API (Admin only)
export const usersAPI = {
  getAll: () => api.get('/users'),
  getPicker: () => api.get('/users/picker'),
  getForPicker: () => api.get('/users/picker'),
  getByRole: (role) => api.get(`/users/role/${role}`),
  getAssignable: (roles = [], campaignId) => api.get('/users/assignable', {
    params: {
      roles: Array.isArray(roles) ? roles.join(',') : roles,
      campaign_id: campaignId || undefined,
    }
  }),
  getOperationsManagers: () => api.get('/users/operations-managers/list'),
  getOne: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  createOperationMember: (data) => api.post('/users/operation-member', data),
  update: (id, data) => api.patch(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  register: (data) => api.post('/users/register', data),
  updatePassword: (id, data) => api.patch(`/users/${id}/password`, data),
  setPassword: (id, password) => api.post(`/users/${id}/set-password`, { password }),
  getCSUsers: () => api.get('/users/cs-users/list'),
};

export const rolesAPI = {
  getCatalog: () => api.get('/roles/catalog'),
  getRoleMenuPermissions: (roleName) => api.get(`/roles/${roleName}/menu-permissions`),
  updateRoleMenuPermissions: (roleName, data) =>
    api.put(`/roles/${roleName}/menu-permissions`, data),
  // Office-team membership + superadmin overrides
  listTeamMembers: () => api.get('/roles/team-members'),
  setUserTeam: (userId, team) => api.patch(`/roles/users/${userId}/team`, { team }),
  setUserSuperadmin: (userId, isSuperadmin) =>
    api.patch(`/roles/users/${userId}/superadmin`, { is_superadmin: Boolean(isSuperadmin) }),
};

// Analytics API — extended below with profitability + CRM funnel/ROI bindings
export const analyticsAPI = {
  getCampaignStatus: () => api.get('/analytics/campaign-status'),
  getExpenseTrend: (days = 30) => api.get('/analytics/expense-trend', { params: { days } }),
  getPaymentsSummary: () => api.get('/analytics/payments-summary'),
  getVendorPerformance: (limit = 10) => api.get('/analytics/vendor-performance', { params: { limit } }),
  getUtilizationSummary: () => api.get('/analytics/utilization-summary'),
};

export const businessIntelligenceAPI = {
  getAccess: () => api.get('/business-intelligence/access').then((res) => res.data),
  getSummary: (periodDays = 30) =>
    api.get('/business-intelligence/summary', { params: { period_days: periodDays } }).then((res) => res.data),
  ask: (question, periodDays = 30) =>
    api.post('/business-intelligence/ask', { question }, { params: { period_days: periodDays } }).then((res) => res.data),
};

// Operation Members API
export const operationsMembersAPI = {
  getAvailableMembers: () => api.get('/operation-members').then(res => res.data),
  getCampaignMembers: (campaignId) => api.get(`/campaign-members/campaign/${campaignId}`).then(res => res.data),
  assignToCampaign: (campaignId, memberIds) => api.post('/campaign-members/assign', { campaign_id: parseInt(campaignId, 10), operation_member_ids: memberIds }).then(res => res.data),
  removeFromCampaign: (campaignId, memberId) => api.delete(`/campaign-members/${memberId}`).then(res => res.data),
  getMyAssignments: () => api.get('/operation-members/my-assignments').then(res => res.data),
};

// Campaign Reports API (Comprehensive Reporting System)
export const campaignReportsAPI = {
  getProjectReport: (projectId) => api.get(`/campaign-reports/project/${projectId}`).then(res => res.data),
  getCampaignReport: (campaignId) => api.get(`/campaign-reports/campaign/${campaignId}`).then(res => res.data),
  getDriverReport: (driverId, params = {}) => api.get(`/campaign-reports/driver/${driverId}`, { params }).then(res => res.data),
  getDriverVehicles: (driverId, params = {}) => api.get(`/campaign-reports/driver/${driverId}/vehicles`, { params }).then(res => res.data),
  getVehicleReport: (vehicleId, params = {}) => api.get(`/campaign-reports/vehicle/${vehicleId}`, { params }).then(res => res.data),
  getFinancialSummary: (params = {}) => api.get('/campaign-reports/financial-summary', { params }).then(res => res.data),
  payExpense: (expenseId, formData) => api.post(`/campaign-reports/expenses/${expenseId}/pay`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }).then(res => res.data),
  getExpensePayments: (params = {}) => api.get('/campaign-reports/expense-payments', { params }).then(res => res.data),
};

// Godown Items API (Warehouse Inventory) — backend routes: /inventory/*
export const godownItemsAPI = {
  // Basic CRUD operations → /inventory router in godowns.py
  getAll: () => api.get('/inventory').then(res => res.data),
  getOne: (id) => api.get(`/inventory/${id}`).then(res => res.data),
  create: (data) => api.post('/inventory', data).then(res => res.data),
  update: (id, data) => api.patch(`/inventory/${id}`, data).then(res => res.data),
  delete: (id) => api.delete(`/inventory/${id}`).then(res => res.data),
  bulkUpload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/inventory/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(res => res.data);
  },

  // Godown-specific inventory
  getByGodown: (godownId) => api.get(`/inventory/godown/${godownId}`).then(res => res.data),
  getLowStockAlerts: () => api.get('/inventory/alerts/low-stock').then(res => res.data),
  getAvailableItems: (godownId) =>
    (godownId ? api.get(`/inventory/godown/${godownId}`) : api.get('/inventory')).then(res => res.data),
  getStockLedger: (params = {}) =>
    api.get('/inventory/stock-ledger', { params }).then(res => res.data),
  getCampaignStockLedger: (campaignId, params = {}) =>
    api.get(`/inventory/stock-ledger/campaign/${campaignId}`, { params }).then(res => res.data),
  getDamagedStockLedger: (params = {}) =>
    api.get('/inventory/stock-ledger/damaged', { params }).then(res => res.data),
  scanAssign: ({ item_code, campaign_id }) => {
    const payload = new URLSearchParams();
    payload.append('item_code', item_code);
    payload.append('action_type', 'assign');
    payload.append('campaign_id', String(campaign_id));
    return api.post('/inventory/scan', payload).then(res => res.data);
  },
  scanReturn: ({ item_code, campaign_id }) => {
    const payload = new URLSearchParams();
    payload.append('item_code', item_code);
    payload.append('action_type', 'return');
    payload.append('campaign_id', String(campaign_id));
    return api.post('/inventory/scan', payload).then(res => res.data);
  },
  scanDamage: ({ item_code, campaign_id, damage_reason, reported_by }) => {
    const payload = new URLSearchParams();
    payload.append('item_code', item_code);
    payload.append('action_type', 'damage');
    payload.append('campaign_id', String(campaign_id));
    payload.append('damage_reason', damage_reason || '');
    if (reported_by) {
      payload.append('reported_by', String(reported_by));
    }
    return api.post('/inventory/scan', payload).then(res => res.data);
  },
  scanRepair: ({ item_code, campaign_id, remarks, reported_by }) => {
    const payload = new URLSearchParams();
    payload.append('item_code', item_code);
    payload.append('action_type', 'repair');
    if (campaign_id) {
      payload.append('campaign_id', String(campaign_id));
    }
    if (remarks) {
      payload.append('remarks', remarks);
    }
    if (reported_by) {
      payload.append('reported_by', String(reported_by));
    }
    return api.post('/inventory/scan', payload).then(res => res.data);
  },
  getDamagedItems: (campaignId) =>
    campaignId
      ? api.get(`/extended/campaigns/${campaignId}/damaged-items`).then(res => res.data)
      : api.get('/extended/damaged-items').then(res => res.data),

  // Campaign inventory operations
  getCampaignInventory: (campaignId) => api.get(`/extended/campaigns/${campaignId}/godown/inventory`).then(res => res.data),
  bulkAssignToCampaign: async (campaignId, items) => {
    return Promise.all(
      items.map(({ item_id, quantity, campaign_location_id }) => {
        const payload = new URLSearchParams();
        payload.append('quantity', String(quantity));
        if (campaign_location_id) payload.append('campaign_location_id', String(campaign_location_id));
        return api.post(`/extended/campaigns/${campaignId}/godown/items/${item_id}/assign`, payload).then(res => res.data);
      })
    );
  },
  bulkReturnFromCampaign: async (campaignId, items) => {
    return Promise.all(
      items.map(({ item_id, quantity, quantity_damaged = 0, campaign_location_id }) => {
        const payload = new URLSearchParams();
        payload.append('quantity_returned', String(quantity));
        payload.append('quantity_damaged', String(quantity_damaged));
        if (campaign_location_id) payload.append('campaign_location_id', String(campaign_location_id));
        return api.post(`/extended/campaigns/${campaignId}/godown/items/${item_id}/return`, payload).then(res => res.data);
      })
    );
  },
  markAsDamaged: (campaignId, itemId, data = {}) => {
    const payload = new URLSearchParams();
    payload.append('quantity', String(data.quantity ?? 1));
    payload.append('damage_reason', data.damage_reason || '');
    if (data.reported_by) {
      payload.append('reported_by', String(data.reported_by));
    }
    if (data.campaign_location_id) {
      payload.append('campaign_location_id', String(data.campaign_location_id));
    }
    return api.post(`/extended/campaigns/${campaignId}/godown/items/${itemId}/damage`, payload).then(res => res.data);
  },
  removeDamagedItem: (campaignId, damageId) =>
    api.delete(`/extended/campaigns/${campaignId}/damaged-items/${damageId}`).then(res => res.data),
};

// Purchase Module API
export const purchaseAPI = {
  // Purchase Requests
  createPR: (data) => api.post('/purchase/requests', data).then(r => r.data),
  listPRs: (params) => api.get('/purchase/requests', { params }).then(r => r.data),
  getPR: (id) => api.get(`/purchase/requests/${id}`).then(r => r.data),
  searchLeads: (params) => api.get('/purchase/leads/search', { params }).then(r => r.data),
  // Narrow project lookup for PRs — bypasses the project visibility scope
  // so a CS / BD raising a PR can attach it to any project (not just ones
  // they're directly on). Returns a tiny brief, not full ProjectResponse.
  searchProjects: (params) => api.get('/purchase/projects/search', { params }).then(r => r.data),
  updatePR: (id, data) => api.patch(`/purchase/requests/${id}`, data).then(r => r.data),
  deletePR: (id) => api.delete(`/purchase/requests/${id}`).then(r => r.data),
  approvePR: (id, remarks) => api.post(`/purchase/requests/${id}/approve`, null, { params: { remarks } }).then(r => r.data),
  rejectPR: (id, remarks) => api.post(`/purchase/requests/${id}/reject`, null, { params: { remarks } }).then(r => r.data),
  // Costing round-trip: best vendor rate per line, and the "send costing to Sales" handoff.
  getPRCosting: (id) => api.get(`/purchase/requests/${id}/costing`).then(r => r.data),
  sendPRCosting: (id) => api.post(`/purchase/requests/${id}/send-costing`).then(r => r.data),
  sendPRToPO: (id) => api.post(`/purchase/requests/${id}/send-to-po`).then(r => r.data),
  leadPendingPO: (leadId) => api.get(`/purchase/leads/${leadId}/pending-po`).then(r => r.data),
  prPoEligibility: (id) => api.get(`/purchase/requests/${id}/po-eligibility`).then(r => r.data),
  // Vendor RFQ Excel template — returns the raw .xlsx blob for download.
  downloadRFQTemplate: (id) =>
    api.get(`/purchase/requests/${id}/rfq-template.xlsx`, { responseType: 'blob' }),

  // RFQs
  createRFQ: (data) => api.post('/purchase/rfqs', data).then(r => r.data),
  // Per-PR-item sourcing status (which items are already on an active RFQ).
  getPRItemRFQStatus: (prId) => api.get(`/purchase/requests/${prId}/rfq-item-status`).then(r => r.data),
  // Create RFQ (if needed) and fire WhatsApp invites via the GHL webhook.
  // Returns per-vendor results so the UI can surface skipped/failed invites.
  inviteVendors: (data) => api.post('/purchase/rfqs/invite', data).then(r => r.data),
  listRFQs: (params) => api.get('/purchase/rfqs', { params }).then(r => r.data),
  getRFQ: (id) => api.get(`/purchase/rfqs/${id}`).then(r => r.data),
  updateRFQ: (id, data) => api.patch(`/purchase/rfqs/${id}`, data).then(r => r.data),
  addVendorQuote: (rfqId, data) => api.post(`/purchase/rfqs/${rfqId}/quotes`, data).then(r => r.data),
  updateVendorQuote: (rfqId, quoteId, data) => api.patch(`/purchase/rfqs/${rfqId}/quotes/${quoteId}`, data).then(r => r.data),
  selectVendorQuote: (rfqId, quoteId) => api.post(`/purchase/rfqs/${rfqId}/quotes/${quoteId}/select`).then(r => r.data),
  getComparison: (rfqId) => api.get(`/purchase/rfqs/${rfqId}/comparison`).then(r => r.data),
  // Upload a vendor's filled RFQ xlsx — multipart with vendor_id + file.
  uploadVendorQuote: (rfqId, vendorId, file, proofFile) => {
    const fd = new FormData();
    fd.append('vendor_id', String(vendorId));
    fd.append('file', file);
    fd.append('proof_file', proofFile);
    return api.post(`/purchase/rfqs/${rfqId}/quotes/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },

  // Purchase Orders
  createPO: (data) => api.post('/purchase/orders', data).then(r => r.data),
  listPOs: (params) => api.get('/purchase/orders', { params }).then(r => r.data),
  getPO: (id) => api.get(`/purchase/orders/${id}`).then(r => r.data),
  vendorBills: (id) => api.get(`/purchase/orders/${id}/vendor-bills`).then(r => r.data),
  billPrefill: (id) => api.get(`/purchase/orders/${id}/bill-prefill`).then(r => r.data),
  updatePO: (id, data) => api.patch(`/purchase/orders/${id}`, data).then(r => r.data),
  approvePO: (id) => api.post(`/purchase/orders/${id}/approve`).then(r => r.data),
  rejectPO: (id, { reason, file } = {}) => {
    const fd = new FormData();
    fd.append('reason', reason ?? '');
    if (file) fd.append('file', file);
    return api.post(`/purchase/orders/${id}/reject`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  resubmitPO: (id) => api.post(`/purchase/orders/${id}/resubmit`).then(r => r.data),
  // Settle / Close a PO IN PLACE. `amount` null/omitted → close only (value
  // untouched); a number → settle to that final value, then close. Returns the
  // SAME PO (id + po_number unchanged); existing payments and bills stay
  // attached. Either way the PO ends CLOSED.
  settleClosePO: (id, { amount = null, reason }) =>
    api.post(`/purchase/orders/${id}/settle-close`, { amount, reason }).then(r => r.data),
  // Server-rendered PO PDF — returns the raw blob for download.
  downloadPOPdf: (id) => api.get(`/purchase/orders/${id}/pdf`, { responseType: 'blob' }),
  // Email-PO via Gmail OAuth
  emailPODefaults: (id) => api.get(`/purchase/orders/${id}/email/defaults`).then(r => r.data),
  sendPOEmail: (id, payload) => api.post(`/purchase/orders/${id}/email`, payload).then(r => r.data),
  listPOEmails: (id) => api.get(`/purchase/orders/${id}/emails`).then(r => r.data),
  gmailStatus: () => api.get(`/purchase/gmail/status`).then(r => r.data),
  // Fetched with auth, returns the Google consent URL the popup should open.
  // We can't redirect-with-auth via window.open, so we go through this hop.
  gmailAuthorizeUrl: () => api.get(`/purchase/gmail/authorize-url`).then(r => r.data),

  // Dashboard
  getDashboardSummary: () => api.get('/purchase/dashboard/summary').then(r => r.data),
  // PO payment tracking: PO Value / Total Paid / Remaining / history.
  poPaymentSummary: (poId) => api.get(`/purchase/orders/${poId}/payment-summary`).then(r => r.data),
  // NOTE: PO/PR FL reconciliation now lives on ordersAPI (poFlMismatches /
  // poFlResync / requestPoFlReview / …) under /orders/reconciliation/po-fl/*.
};

// PO Payment Requests — raise → manager approve → Accounts process, linked to a PO.
export const poPaymentRequestsAPI = {
  create: (data) => api.post('/po-payment-requests', data).then(r => r.data),
  list: (params) => api.get('/po-payment-requests', { params }).then(r => r.data),
  mine: () => api.get('/po-payment-requests/mine').then(r => r.data),
  pendingApproval: () => api.get('/po-payment-requests/pending-approval').then(r => r.data),
  // PO payment requests THIS user actioned as approver/processor — their payment
  // approval history (the Plus-1 manager decision, which isn't a workflow step).
  myApprovals: () => api.get('/po-payment-requests/my-approvals').then(r => r.data),
  getOne: (id) => api.get(`/po-payment-requests/${id}`).then(r => r.data),
  approve: (id, data = {}) => api.patch(`/po-payment-requests/${id}/approve`, data).then(r => r.data),
  // Reject — mandatory reason + optional supporting file (multipart form).
  reject: (id, { reason, file } = {}) => {
    const fd = new FormData();
    fd.append('reason', reason ?? '');
    if (file) fd.append('file', file);
    return api.patch(`/po-payment-requests/${id}/reject`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  process: (id, data) => api.patch(`/po-payment-requests/${id}/process`, data).then(r => r.data),
  // Accounts records TDS deducted on an approved request (0 <= tds < gross).
  // Gross stays put (PO rollup unaffected); only net payable / bank export change.
  setTds: (id, tdsAmount) => api.patch(`/po-payment-requests/${id}/tds`, { tds_amount: tdsAmount }).then(r => r.data),
  // Owner edit (amount / remarks) + resubmit for the rejection → correction flow.
  update: (id, data) => api.patch(`/po-payment-requests/${id}`, data).then(r => r.data),
  resubmit: (id) => api.patch(`/po-payment-requests/${id}/resubmit`, {}).then(r => r.data),
  // The vendor's account beside a request: does the vendor already hold our
  // money, and the most a request can be. For a new request on a PO, or for
  // one request (approval / Accounts; `utr` when marking paid).
  vendorAccount: (poId) => api.get('/po-payment-requests/vendor-account', { params: { po_id: poId } }).then(r => r.data),
  requestVendorAccount: (id, utr) => api.get(`/po-payment-requests/${id}/vendor-account`, {
    params: utr ? { utr } : {},
  }).then(r => r.data),
  // Every request in my approval queue at once — {request id: account}.
  pendingApprovalVendorAccounts: () => api.get('/po-payment-requests/pending-approval/vendor-accounts').then(r => r.data),
  // Accounts let a request past the vendor's advance, with a written reason.
  overrideVendorCap: (id, reason) => api.patch(`/po-payment-requests/${id}/vendor-cap-override`, { reason }).then(r => r.data),
  // Download the bank sheet (.xlsx) for a request; triggers a browser download.
  downloadBankSheet: async (id, filename) => {
    const res = await api.get(`/po-payment-requests/${id}/download`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `bank_sheet_${id}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
  // Bulk Accounts flow: merged download + UTR upload reconcile.
  downloadMerged: async (requestIds, filename) => {
    const res = await api.post('/po-payment-requests/download-merged', { request_ids: requestIds }, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'po_bank_sheet_merged.xlsx';
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  },
  listExports: () => api.get('/po-payment-requests/exports').then(r => r.data),
  redownloadExport: async (batchId, filename) => {
    const res = await api.get(`/po-payment-requests/exports/${batchId}/download`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url; a.download = filename || `po_bank_sheet_${batchId}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
    // Request ids now past the vendor's cap (the sheet downloads regardless).
    const held = res.headers?.['x-vendor-cap-held'] || '';
    return held.split(',').map((x) => x.trim()).filter(Boolean);
  },
  reconcilePreview: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/po-payment-requests/reconcile/preview', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  reconcileApply: (payload) => api.post('/po-payment-requests/reconcile/apply', payload).then(r => r.data),
  reconcileHistory: () => api.get('/po-payment-requests/reconcile/history').then(r => r.data),
  downloadTemplate: async () => {
    const res = await api.get('/po-payment-requests/reconcile/template', { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url; a.download = 'po_payment_upload_template.xlsx';
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  },
  listAttachments: (id) => api.get(`/po-payment-requests/${id}/attachments`).then(r => r.data),
  uploadAttachment: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/po-payment-requests/${id}/attachments/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  deleteAttachment: (id, attachmentId) => api.delete(`/po-payment-requests/${id}/attachments/${attachmentId}`),
};

// Project NOC API
export const projectNOCAPI = {
  initiate: (projectId) => api.post(`/project-noc/${projectId}/initiate`).then(r => r.data),
  getNOCs: (projectId) => api.get(`/project-noc/${projectId}`).then(r => r.data),
  getSummary: (projectId) => api.get(`/project-noc/${projectId}/summary`).then(r => r.data),
  reviewNOC: (nocId, data) => api.patch(`/project-noc/${nocId}/review`, data).then(r => r.data),
  reInitiate: (projectId) => api.post(`/project-noc/${projectId}/re-initiate`).then(r => r.data),
  // Cross-project NOC list for the Project Closure page (role-scoped server-side).
  getQueue: (params) => api.get('/project-noc/queue', { params }).then(r => r.data),
  // Unfiltered totals for the same board, for the sidebar badge. Same scope and
  // the same permission gate as getQueue, so the badge cannot show work the
  // page would not list.
  getQueueCounts: () => api.get('/project-noc/queue/counts').then(r => r.data),
};

export const relationshipMapAPI = {
  get: (entityType, entityId) => api.get(`/relationship-map/${entityType}/${encodeURIComponent(entityId)}`).then(r => r.data),
};

// OFM Client Portal integration (feature-flagged; every call 404s when the
// backend flag is off, which the UI treats as "feature disabled").
export const ofmAPI = {
  // Probe whether the integration is live. Rejects (404) when disabled.
  status: () => api.get('/ofm/status').then(r => r.data),
  // Mint an SSO code for this project and get the OFM landing URL to open.
  authorize: (projectId) =>
    api.get('/ofm/sso/ofm/authorize', { params: { project_id: projectId } }).then(r => r.data),
  // Requests raised in the portal, mirrored against this project.
  listRequests: (projectId) => api.get(`/ofm/projects/${projectId}/requests`).then(r => r.data),
  // Reject a pending request; fires the callback to OFM. ("created" is not
  // settable here — it happens only via linkPR, once a PR actually exists.)
  decide: (requestId, decision, remarks) =>
    api.post(`/ofm/requests/${requestId}/decision`, { decision, remarks }).then(r => r.data),
  // Link the PR raised from this request → marks it Created + notifies OFM.
  // Idempotent; one PR per request.
  linkPR: (requestId, purchaseRequestId) =>
    api.post(`/ofm/requests/${requestId}/link-pr`, { purchase_request_id: purchaseRequestId })
      .then(r => r.data),
  // OFM fast-lane: create the PO + full-amount payment request for Accounts in
  // one action. Marks the request Created + notifies OFM. Idempotent.
  createPO: (requestId, vendorId) =>
    api.post(`/ofm/requests/${requestId}/create-po`, { vendor_id: vendorId }).then(r => r.data),
  // Chitra's dedicated OFM page: every OFM request (finance_control only).
  // Optional status filter: pending | created | rejected.
  listAll: (status) =>
    api.get('/ofm/requests', { params: status ? { status } : {} }).then(r => r.data),
  // One OFM request with full business context + linked PR/PO/Payment + history
  // (for the OFM Details page). Finance-control only.
  get: (requestId) =>
    api.get(`/ofm/requests/${requestId}`).then(r => r.data),
  // UPI/QR self-pay (Chitra): record the payment she made. Creates + PROCESSES the
  // OFM PO's payment (Transaction ID stored in transaction_ref). Finance-control only.
  makePayment: (requestId, transactionId, remarks) =>
    api.post(`/ofm/requests/${requestId}/make-payment`,
             { transaction_id: transactionId, remarks: remarks || null }).then(r => r.data),
  // The OFM request linked to a PR — read-only status/linkage for PR Details.
  byPR: (purchaseRequestId) =>
    api.get(`/ofm/requests/by-pr/${purchaseRequestId}`).then(r => r.data),
};

// Tasks (Flowlu-modeled task module)
export const tasksAPI = {
  // Workflows + tags
  listWorkflows: () => api.get('/tasks/workflows').then(res => res.data),
  listTags: () => api.get('/tasks/tags').then(res => res.data),
  createTag: (data) => api.post('/tasks/tags', data).then(res => res.data),

  // Tasks CRUD + filters
  list: (params) => api.get('/tasks', { params }).then(res => res.data),
  getOne: (id) => api.get(`/tasks/${id}`).then(res => res.data),
  listLifecycle: (id) => api.get(`/tasks/${id}/lifecycle`).then(res => res.data),
  // Delivery ratings (1–5): assigner + manager levels
  getRatings: (id) => api.get(`/tasks/${id}/ratings`).then(res => res.data),
  rate: (id, data) => api.post(`/tasks/${id}/rate`, data).then(res => res.data),
  setLate: (id, data) => api.post(`/tasks/${id}/late`, data).then(res => res.data),
  create: (data) => api.post('/tasks', data).then(res => res.data),
  update: (id, data) => api.patch(`/tasks/${id}`, data).then(res => res.data),
  delete: (id) => api.delete(`/tasks/${id}`),
  move: (id, stage_id) => api.post(`/tasks/${id}/move`, { stage_id }).then(res => res.data),

  // Comments
  listComments: (taskId) => api.get(`/tasks/${taskId}/comments`).then(res => res.data),
  addComment: (taskId, data) => api.post(`/tasks/${taskId}/comments`, data).then(res => res.data),
  deleteComment: (commentId) => api.delete(`/tasks/comments/${commentId}`),

  // Checklist
  addChecklistItem: (taskId, data) => api.post(`/tasks/${taskId}/checklist`, data).then(res => res.data),
  updateChecklistItem: (itemId, data) => api.patch(`/tasks/checklist/${itemId}`, data).then(res => res.data),
  deleteChecklistItem: (itemId) => api.delete(`/tasks/checklist/${itemId}`),

  // Attachments
  listAttachments: (taskId) => api.get(`/tasks/${taskId}/attachments`).then(res => res.data),
  addAttachment: (taskId, data) => api.post(`/tasks/${taskId}/attachments`, data).then(res => res.data),
  uploadAttachment: (taskId, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/tasks/${taskId}/attachments/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
        ? (e) => onProgress(Math.round((e.loaded * 100) / (e.total || e.loaded || 1)))
        : undefined,
    }).then(res => res.data);
  },
  deleteAttachment: (attId) => api.delete(`/tasks/attachments/${attId}`),

  // Flowlu import (idempotent upsert by flowlu_id)
  importFlowlu: (data) => api.post('/tasks/import/flowlu', data).then(res => res.data),
};

// Reflection of the Day — daily journal wrapper around tasks.
export const reflectionsAPI = {
  get: (date) => api.get('/reflections', { params: date ? { date } : {} }).then((r) => r.data),
  recent: (days = 7) => api.get('/reflections/recent', { params: { days } }).then((r) => r.data),
  save: (payload) => api.put('/reflections', payload).then((r) => r.data),
};

// CRM (Pipelines / Stages / Sources / Accounts / Leads — Flowlu CRM mirror)
export const crmAPI = {
  // Pipelines + stages
  listPipelines: () => api.get('/crm/pipelines').then(r => r.data),
  createPipeline: (data) => api.post('/crm/pipelines', data).then(r => r.data),
  updatePipeline: (id, data) => api.patch(`/crm/pipelines/${id}`, data).then(r => r.data),
  listStages: (params) => api.get('/crm/stages', { params }).then(r => r.data),
  createStage: (data) => api.post('/crm/stages', data).then(r => r.data),
  updateStage: (id, data) => api.patch(`/crm/stages/${id}`, data).then(r => r.data),

  // Sources
  listSources: () => api.get('/crm/sources').then(r => r.data),
  createSource: (data) => api.post('/crm/sources', data).then(r => r.data),

  // Accounts
  listAccounts: (params) => api.get('/crm/accounts', { params }).then(r => r.data),
  getAccount: (id) => api.get(`/crm/accounts/${id}`).then(r => r.data),
  createAccount: (data) => api.post('/crm/accounts', data).then(r => r.data),
  updateAccount: (id, data) => api.patch(`/crm/accounts/${id}`, data).then(r => r.data),

  // Leads (kanban cards)
  listLeads: (params) => api.get('/crm/leads', { params }).then(r => r.data),
  summary: (params) => api.get('/analytics/crm/summary', { params }).then(r => r.data),
  getLead: (id) => api.get(`/crm/leads/${id}`).then(r => r.data),
  createLead: (data) => api.post('/crm/leads', data).then(r => r.data),
  updateLead: (id, data) => api.patch(`/crm/leads/${id}`, data).then(r => r.data),
  // Standardized loss reasons for the "Mark as Lost" dropdown.
  getLostReasons: () => api.get('/crm/lost-reasons').then(r => r.data),
  moveLead: (id, payload) => api.post(`/crm/leads/${id}/move`, payload).then(r => r.data),
  linkLeadAccountReceivable: (id, clientId) =>
    api.post(`/crm/leads/${id}/link-account-receivable`, { client_id: clientId }).then(r => r.data),
  deleteLead: (id) => api.delete(`/crm/leads/${id}`),

  // Sync (admin-only — pulls from Flowlu)
  sync: (params) => api.post('/crm/sync', null, { params }).then(r => r.data),
  syncLeads: (params) => api.post('/crm/sync/leads', null, { params }).then(r => r.data),

  // Estimates — locally-owned (Phase A read; Phase B CRUD)
  listLeadEstimates: (leadId) => api.get(`/crm/leads/${leadId}/estimates`).then(r => r.data),
  getEstimate: (id) => api.get(`/crm/estimates/${id}`).then(r => r.data),
  createEstimate: (payload) => api.post('/crm/estimates', payload).then(r => r.data),
  updateEstimate: (id, payload) => api.patch(`/crm/estimates/${id}`, payload).then(r => r.data),
  setEstimateStatus: (id, status) =>
    api.post(`/crm/estimates/${id}/status`, null, { params: { status } }).then(r => r.data),
  // Reopen a converted (locked) estimate so Sales can revise scope after billing
  // started. Audited server-side; does not change the order value.
  reopenEstimate: (id) =>
    api.post(`/crm/estimates/${id}/reopen`).then(r => r.data),
  deleteEstimate: (id) => api.delete(`/crm/estimates/${id}`),
  // Convert an Accepted/Sent estimate into a Draft invoice. Server flips the
  // source estimate's status to 'converted' atomically.
  convertEstimateToInvoice: (estimateId) =>
    api.post(`/crm/estimates/${estimateId}/convert-to-invoice`).then(r => r.data),
  // Re-invoice an estimate for (part of) the order's pending balance.
  // amount is optional — omit to bill the full remaining balance.
  reinvoiceEstimate: (estimateId, amount) =>
    api.post(`/crm/estimates/${estimateId}/reinvoice`,
      amount != null ? { amount } : {}).then(r => r.data),
  // Draft an invoice for an order's pending balance (copies the order's estimate items).
  createInvoiceFromOrder: (orderId) =>
    api.post(`/crm/orders/${orderId}/create-invoice`).then(r => r.data),

  // Invoices (Phase B.4) — same shape as estimates, separate routes
  listLeadInvoices: (leadId) => api.get(`/crm/leads/${leadId}/invoices`).then(r => r.data),
  // Consolidated list of ALL invoices for the Invoices page (client-side search/filter/sort).
  listInvoices: (params = {}) => api.get('/crm/invoices', { params }).then(r => r.data),
  // Vendor costing rounds for a lead (best rate/vendor per line, per PR).
  listLeadCosting: (leadId) => api.get(`/crm/leads/${leadId}/costing`).then(r => r.data),
  listEstimateVersions: (estimateId) => api.get(`/crm/estimates/${estimateId}/versions`).then(r => r.data),

  // Estimate Templates — reusable, categorised, approval-gated line-item bundles.
  listEstimateTemplateCategories: () => api.get('/crm/estimate-templates/categories').then(r => r.data),
  listEstimateTemplates: (params = {}) => api.get('/crm/estimate-templates', { params }).then(r => r.data),
  getEstimateTemplate: (id) => api.get(`/crm/estimate-templates/${id}`).then(r => r.data),
  createEstimateTemplate: (payload) => api.post('/crm/estimate-templates', payload).then(r => r.data),
  listPendingEstimateTemplates: () => api.get('/crm/estimate-templates/pending').then(r => r.data),
  listMyEstimateTemplates: () => api.get('/crm/estimate-templates/mine').then(r => r.data),
  approveEstimateTemplate: (id) => api.post(`/crm/estimate-templates/${id}/approve`).then(r => r.data),
  rejectEstimateTemplate: (id, reason) => api.post(`/crm/estimate-templates/${id}/reject`, { reason }).then(r => r.data),
  deleteEstimateTemplate: (id) => api.delete(`/crm/estimate-templates/${id}`),
  getInvoice: (id) => api.get(`/crm/invoices/${id}`).then(r => r.data),
  // Proforma PDF as a blob (auth header travels with the axios client; UI opens it).
  downloadInvoicePdf: (id) =>
    api.get(`/crm/invoices/${id}/pdf`, { responseType: 'blob' }).then(r => r.data),
  createInvoice: (payload) => api.post('/crm/invoices', payload).then(r => r.data),
  updateInvoice: (id, payload) => api.patch(`/crm/invoices/${id}`, payload).then(r => r.data),
  setInvoiceStatus: (id, status) =>
    api.post(`/crm/invoices/${id}/status`, null, { params: { status } }).then(r => r.data),
  deleteInvoice: (id) => api.delete(`/crm/invoices/${id}`),

  // Billing pipeline (CS → Accounts)
  getBillingProgress: (leadId) =>
    api.get(`/crm/leads/${leadId}/billing-progress`).then(r => r.data),
  sendInvoiceToAccounts: (id, payload) =>
    api.post(`/crm/invoices/${id}/send-to-accounts`, payload).then(r => r.data),
  rejectInvoiceBilling: (id, payload) =>
    api.post(`/crm/invoices/${id}/reject-billing`, payload).then(r => r.data),
  approveInvoiceBilling: (id, payload) =>
    api.post(`/crm/invoices/${id}/approve-billing`, payload).then(r => r.data),
  listPendingBilling: () =>
    api.get('/crm/invoices/pending-billing').then(r => r.data),

  // CRM invoice attachments (Client PO + supporting docs)
  listInvoiceAttachments: (id) =>
    api.get(`/crm/invoices/${id}/attachments`).then(r => r.data),
  uploadInvoiceAttachment: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/crm/invoices/${id}/attachments/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  deleteInvoiceAttachment: (id, attachmentId) =>
    api.delete(`/crm/invoices/${id}/attachments/${attachmentId}`),

  // Client POs received against a lead (number/date/value + document).
  listClientPOs: (leadId) => api.get(`/crm/leads/${leadId}/client-pos`).then(r => r.data),
  // Global Client PO list (enriched with customer + order/project) for the Client PO page.
  listAllClientPOs: (params) => api.get('/crm/client-pos', { params }).then(r => r.data),
  // `formData` is a multipart FormData (po_number, po_date, po_value, file).
  createClientPO: (leadId, formData) =>
    api.post(`/crm/leads/${leadId}/client-pos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data),
  deleteClientPO: (clientPoId) => api.delete(`/crm/client-pos/${clientPoId}`),

  // Helper that opens an authenticated PDF in a new tab. Used for both
  // estimates and invoices.
  openCrmPdf: async (kind, id, filename) => {
    const path = kind === 'invoice' ? `/crm/invoices/${id}/pdf` : `/crm/estimates/${id}/pdf`;
    const r = await api.get(path, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
    const w = window.open(url, '_blank');
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    if (!w) {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `${kind}_${id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    }
  },
  // Backwards-compat shim used by existing callsites.
  openEstimatePdf: function (id, filename) { return this.openCrmPdf('estimate', id, filename); },

  // Products & Services (Phase B.6)
  listProducts: (params) => api.get('/crm/products-services', { params }).then(r => r.data),
  createProduct: (data) => api.post('/crm/products-services', data).then(r => r.data),
  updateProduct: (id, data) => api.patch(`/crm/products-services/${id}`, data).then(r => r.data),
  deleteProduct: (id) => api.delete(`/crm/products-services/${id}`),

  listLeadProducts: (leadId) => api.get(`/crm/leads/${leadId}/products`).then(r => r.data),
  replaceLeadProducts: (leadId, items) =>
    api.put(`/crm/leads/${leadId}/products`, { items }).then(r => r.data),

  // Stage history (every drag-drop / move_lead writes here)
  listLeadStageHistory: (leadId) => api.get(`/crm/leads/${leadId}/stage-history`).then(r => r.data),

  // Comments — locally-owned activity stream
  listLeadComments: (leadId) => api.get(`/crm/leads/${leadId}/comments`).then(r => r.data),
  createLeadComment: (leadId, body) =>
    api.post(`/crm/leads/${leadId}/comments`, { body }).then(r => r.data),

  // Quotes (versioned, attached to a lead during the Negotiation stage)
  listQuotes: (leadId) => api.get(`/crm/leads/${leadId}/quotes`).then(r => r.data),
  createQuote: (leadId, data) =>
    api.post(`/crm/leads/${leadId}/quotes`, { ...data, lead_id: leadId }).then(r => r.data),
  updateQuote: (quoteId, data) =>
    api.patch(`/crm/quotes/${quoteId}`, data).then(r => r.data),
  deleteQuote: (quoteId) => api.delete(`/crm/quotes/${quoteId}`),

  // Convert Lead → Order (issues FL number, creates Project shell)
  convertLead: (leadId, data) =>
    api.post(`/crm/leads/${leadId}/convert`, data).then(r => r.data),
};

// Orders — created on Lead → Won. Owner of FL000XXX number issuance.
export const ordersAPI = {
  list: (params) => api.get('/orders', { params }).then(r => r.data),
  financials: () => api.get('/orders/financials').then(r => r.data),
  get: (id) => api.get(`/orders/${id}`).then(r => r.data),
  requestInvoice: (id) => api.post(`/orders/${id}/request-invoice`).then(r => r.data),
  getByFlNumber: (fl) => api.get(`/orders/by-fl/${fl}`).then(r => r.data),
  getByLeadId: (leadId) => api.get(`/orders/by-lead/${leadId}`).then(r => r.data),
  update: (id, data) => api.patch(`/orders/${id}`, data).then(r => r.data),
  listAmendments: (orderId) => api.get(`/orders/${orderId}/amendments`).then(r => r.data),
  // Approval inbox — amendments waiting on the current user, scoped by org hierarchy.
  listPendingAmendmentsForMe: () =>
    api.get('/orders/amendments/pending-for-me').then(r => r.data),
  createAmendment: (orderId, data) => api.post(`/orders/${orderId}/amendments`, data).then(r => r.data),
  approveAmendment: (orderId, amendmentId, data = {}) =>
    api.post(`/orders/${orderId}/amendments/${amendmentId}/approve`, data).then(r => r.data),
  rejectAmendment: (orderId, amendmentId, data = {}) =>
    api.post(`/orders/${orderId}/amendments/${amendmentId}/reject`, data).then(r => r.data),
  // Read-only peek at the next FL number that would be issued.
  peekNextFlNumber: () => api.get('/orders/next-fl-number').then(r => r.data),
  // Optional per-user reconciliation plugin.
  reconciliationCandidates: () => api.get('/orders/reconciliation/mine').then(r => r.data),
  reconciliationDashboard: () => api.get('/orders/reconciliation/dashboard').then(r => r.data),
  raiseReconciliation: (orderId) => api.post(`/orders/reconciliation/${orderId}/raise`, {}).then(r => r.data),
  reconcileBilling: (invoiceId) => api.post(`/orders/reconciliation/billing/${invoiceId}/reconcile`, {}).then(r => r.data),
  correctPostedBilling: (invoiceId) => api.post(`/orders/reconciliation/billing/${invoiceId}/correct-posted`, {}).then(r => r.data),
  // Billing-correction hand-off (CS/BD/owner ↔ Accounts). None of these change the
  // invoice; they append to a request thread and notify. Submit/reject are
  // multipart so an optional (reject: optional) attachment can ride along.
  requestBillingCorrection: (invoiceId, { note, file } = {}) => {
    const fd = new FormData();
    if (note) fd.append('note', note);
    if (file) fd.append('file', file);
    return api.post(`/orders/reconciliation/billing/${invoiceId}/request-correction`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  listBillingCorrectionRequests: (status = 'open') => api.get('/orders/reconciliation/billing/correction-requests', { params: { status } }).then(r => r.data),
  billingCorrectionRequestsSummary: () => api.get('/orders/reconciliation/billing/correction-requests/summary').then(r => r.data),
  listMyBillingCorrectionRequests: (status) => api.get('/orders/reconciliation/billing/my-correction-requests', { params: status ? { status } : {} }).then(r => r.data),
  rejectBillingCorrectionRequest: (requestId, { reason, file } = {}) => {
    const fd = new FormData();
    fd.append('reason', reason || '');
    if (file) fd.append('file', file);
    return api.post(`/orders/reconciliation/billing/correction-requests/${requestId}/reject`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  cancelBillingCorrectionRequest: (requestId) => api.post(`/orders/reconciliation/billing/correction-requests/${requestId}/cancel`, {}).then(r => r.data),
  // PO/PR FL reconciliation — a PO's fl_number is denormalized from its PR's
  // project; if the PR's project is edited later the cache goes stale. Scoped to
  // the caller's accessible projects. Safe cases self-correct (poFlResync);
  // finance-sensitive ones (vendor bills / processed payments) route to the
  // Accounts Finance-review queue. Only fl_number caches are ever rewritten.
  poFlMismatches: () => api.get('/orders/reconciliation/po-fl/mismatches').then(r => r.data),
  poFlPreview: (poId) => api.get(`/orders/reconciliation/po-fl/${poId}/preview`).then(r => r.data),
  poFlResync: (poId) => api.post(`/orders/reconciliation/po-fl/${poId}/resync`, {}).then(r => r.data),
  // Finance Review hand-off — mirrors the billing correction request/reject/
  // resolve flow: optional note + optional attachment, mandatory reject reason,
  // append-only history. None of these change PO/payment financial data; resolve
  // applies the correction only through the guarded reconciliation service.
  requestPoFlReview: (poId, { note, file } = {}) => {
    const fd = new FormData();
    if (note) fd.append('note', note);
    if (file) fd.append('file', file);
    return api.post(`/orders/reconciliation/po-fl/${poId}/request-review`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  listPoFlReviewRequests: (status = 'open') => api.get('/orders/reconciliation/po-fl/review-requests', { params: { status } }).then(r => r.data),
  poFlReviewRequestsSummary: () => api.get('/orders/reconciliation/po-fl/review-requests/summary').then(r => r.data),
  listMyPoFlReviewRequests: (status) => api.get('/orders/reconciliation/po-fl/my-review-requests', { params: status ? { status } : {} }).then(r => r.data),
  resolvePoFlReviewRequest: (requestId, { note } = {}) => {
    const fd = new FormData();
    if (note) fd.append('note', note);
    return api.post(`/orders/reconciliation/po-fl/review-requests/${requestId}/resolve`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  rejectPoFlReviewRequest: (requestId, { reason, file } = {}) => {
    const fd = new FormData();
    fd.append('reason', reason || '');
    if (file) fd.append('file', file);
    return api.post(`/orders/reconciliation/po-fl/review-requests/${requestId}/reject`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  cancelPoFlReviewRequest: (requestId) => api.post(`/orders/reconciliation/po-fl/review-requests/${requestId}/cancel`, {}).then(r => r.data),
};

export const targetAccountsAPI = {
  list: (params) => api.get('/target-accounts', { params }).then(r => r.data),
  dashboard: (params) => api.get('/target-accounts/dashboard', { params }).then(r => r.data),
  create: (data) => api.post('/target-accounts', data).then(r => r.data),
  update: (id, data) => api.patch(`/target-accounts/${id}`, data).then(r => r.data),
  convertToOpportunity: (id, data) =>
    api.post(`/target-accounts/${id}/convert-to-opportunity`, data).then(r => r.data),
  legacyCandidates: (params) => api.get('/target-accounts/legacy-candidates', { params }).then(r => r.data),
  reclassifyLegacy: (data) => api.post('/target-accounts/reclassify-legacy', data).then(r => r.data),
};

// Expense Heads — chart-of-accounts dropdowns for PRs / Expenses (seeded from Tally).
export const expenseHeadsAPI = {
  list: (params) => api.get('/expense-heads', { params }).then(r => r.data),
  get: (id) => api.get(`/expense-heads/${id}`).then(r => r.data),
};

// Vendor Roles — multi-role assignments (decorator + flex_printer + tent_house, etc.)
export const vendorRolesAPI = {
  list: (vendorId) => api.get(`/vendors/${vendorId}/roles`).then(r => r.data),
  set: (vendorId, roles) =>
    api.put(`/vendors/${vendorId}/roles`, { roles }).then(r => r.data),
};

// Campaign Vendors — service vendors attached to a campaign by role.
export const campaignVendorsAPI = {
  list: (campaignId) => api.get(`/campaigns/${campaignId}/vendors`).then(r => r.data),
  attach: (campaignId, data) =>
    api.post(`/campaigns/${campaignId}/vendors`, data).then(r => r.data),
  update: (cvId, data) =>
    api.patch(`/campaigns/vendors/${cvId}`, data).then(r => r.data),
  detach: (cvId) => api.delete(`/campaigns/vendors/${cvId}`),
  // Filter the vendor master by role for the picker.
  vendorsByRole: (role) =>
    api.get('/vendors/by-role', { params: { role } }).then(r => r.data),
};

// Tally Voucher Mappings — finance can reroute Tally vouchers booked under the wrong FL.
// All endpoints require FINANCE_VIEW; UI is superadmin-only.
export const tallyMappingsAPI = {
  list: () => api.get('/orders/tally-mappings').then(r => r.data),
  create: (data) => api.post('/orders/tally-mappings', data).then(r => r.data),
  deactivate: (id) => api.delete(`/orders/tally-mappings/${id}`).then(r => r.data),
};

// Tally Imports — Sales/Purchase Register monthly XLSX uploads.
export const tallyImportsAPI = {
  // ── On-demand XML sync (button → LAN agent → popup) ──
  requestSync: () => api.post('/tally/sync/jobs').then(r => r.data),
  getSyncJob: (id) => api.get(`/tally/sync/jobs/${id}`).then(r => r.data),
  latestSyncJob: () => api.get('/tally/sync/jobs/latest').then(r => r.data),
  // Payments Tracker — per-party running account (paid vs expensed → balance)
  paymentsTracker: () => api.get('/tally/sync/payments-tracker').then(r => r.data),

  uploadSales: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/tally/sales', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  uploadPurchase: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/tally/purchase', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  uploadCreditNotes: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/tally/credit-notes', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  uploadJournal: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/tally/journal', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  listVouchers: (params) => api.get('/tally/vouchers', { params }).then(r => r.data),
  summary: () => api.get('/tally/summary').then(r => r.data),
};

// FL Reports — per-cost-centre P&L + Retainer dashboard.
// Backend mounts these under `/fl-reports` (not `/reports`) to avoid a
// collision with the legacy reports router's `/reports/{report_id:int}`.
export const flReportsAPI = {
  listFlPnl: (params) => api.get('/fl-reports/fl-pnl', { params }).then(r => r.data),
  getFlPnl: (flNumber, params) =>
    api.get(`/fl-reports/fl-pnl/${flNumber}`, { params }).then(r => r.data),
  // T1 — vendor-wise realized bills (Without-GST) + open PO commitment for an FL.
  vendorBills: (flNumber, params) =>
    api.get(`/fl-reports/fl-pnl/${flNumber}/vendors`, { params }).then(r => r.data),
  // The rows behind one figure on the FL page — see backend fl_pnl_proof.
  // `params` carries the page's dates (period_start / period_end), so a
  // proof adds up to the tile it was opened from.
  figureProof: (flNumber, figure, params) =>
    api.get(`/fl-reports/fl-pnl/${flNumber}/proof/${figure}`, { params }).then(r => r.data),
  // Payment allocations — cash placed against the bills it cleared. The
  // queue is what no rule could prove; the placed list is a rule's (or a
  // person's) work, undoable.
  unallocatedPayments: (params) =>
    api.get('/fl-reports/payments/unallocated', { params }).then(r => r.data),
  allocatePayment: (financeVoucherId, body) =>
    api.post(`/fl-reports/payments/${financeVoucherId}/allocate`, body).then(r => r.data),
  listAllocations: (params) =>
    api.get('/fl-reports/payment-allocations', { params }).then(r => r.data),
  // What the queue does NOT offer, with why: an employee's expenses, a van advance, tax through the CA, rent, salary…
  leftOutPayments: (params) =>
    api.get('/fl-reports/payments/left-out', { params }).then(r => r.data),
  retireAllocation: (allocationId) =>
    api.post(`/fl-reports/payment-allocations/${allocationId}/retire`).then(r => r.data),
  // The vendor's account as an accountant reads it: bills, payments, adjusted / on account / pending, balance vs Tally.
  pendingVendors: () => api.get('/fl-reports/vendors/pending').then(r => r.data),
  vendorBillWise: (params) => api.get('/fl-reports/vendors/bill-wise', { params }).then(r => r.data),
  // Bills against POs, by hand — link (optionally with the bill's share for that PO), and unlink.
  linkBillToPO: (body) => api.post('/fl-reports/po-links', body).then(r => r.data),
  unlinkPO: (linkId) => api.post(`/fl-reports/po-links/${linkId}/retire`).then(r => r.data),
  // Every bill on a PO: this year's invoices with none (why, and the vendor's POs to pick), the
  // one-click link for every line of an invoice, and linked invoices worth a second look.
  billsWithoutPo: (params) => api.get('/fl-reports/bills/without-po', { params }).then(r => r.data),
  billPoReview: () => api.get('/fl-reports/bills/po-review').then(r => r.data),
  linkInvoiceToPO: (body) => api.post('/fl-reports/bills/link-po', body).then(r => r.data),
  // Resolve — one action driven by one search: bills, POs (with vendor, project and client) and projects;
  // adjust against a bill or PO, put on a project, park with a reason, or replace what is already there.
  resolveSearch: (params) => api.get('/fl-reports/resolve/search', { params }).then(r => r.data),
  suggestParkKind: (note) => api.get('/fl-reports/resolve/suggest-kind', { params: { note } }).then(r => r.data),
  paymentPlacement: (financeVoucherId) =>
    api.get(`/fl-reports/payments/${financeVoucherId}/placement`).then(r => r.data),
  resolvePayment: (financeVoucherId, body) =>
    api.post(`/fl-reports/payments/${financeVoucherId}/resolve`, body).then(r => r.data),
  endResolution: (resolutionId, body) =>
    api.post(`/fl-reports/resolutions/${resolutionId}/end`, body || {}).then(r => r.data),
  moveInvoicePO: (body) => api.post('/fl-reports/bills/move-po', body).then(r => r.data),
  // A bill no PO in the app can hold: park it with a reason (last year's PO, one-off, other), note "PO being
  // raised", or pull a parked bill back onto the list. Cost is never touched.
  parkBill: (body) => api.post('/fl-reports/bills/park', body).then(r => r.data),
  endBillResolution: (resolutionId, body) =>
    api.post(`/fl-reports/bills/resolutions/${resolutionId}/end`, body || {}).then(r => r.data),
  // Overheads, parked: month × Tally's P&L head, each year beside Tally's figure; and the vouchers behind a row or cell.
  overheads: () => api.get('/fl-reports/overheads').then(r => r.data),
  overheadDetail: (params) => api.get('/fl-reports/overheads/detail', { params }).then(r => r.data),
  retainerDashboard: () => api.get('/fl-reports/retainers').then(r => r.data),
  retainerSchedule: (orderId) =>
    api.get(`/fl-reports/retainers/${orderId}/schedule`).then(r => r.data),
};

// Employee directory + xlsx import (Admin/HR)
export const employeesAPI = {
  list: (params) => api.get('/employees', { params }).then(r => r.data),
  getMe: () => api.get('/employees/me').then(r => r.data),
  get: (id) => api.get(`/employees/${id}`).then(r => r.data),
  orgTree: () => api.get('/employees/org-tree').then(r => r.data),
  previewImport: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/employees/import/preview', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  applyImport: (file, onlyActive = true) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/employees/import/apply', fd, {
      params: { only_active: onlyActive },
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  // Business Development scoped — same xlsx, but only Department == "Business
  // Development" rows are imported and each gets the default password 123456
  // (hashed). Other departments are untouched.
  previewBusinessDevelopment: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/employees/import/preview-business-development', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  applyBusinessDevelopment: (file, onlyActive = false) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/employees/import/apply-business-development', fd, {
      params: { only_active: onlyActive },
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
};

// Support / Feature Requests / My Account
export const myAccountAPI = {
  getProfile: () => api.get('/my-account/profile').then(r => r.data),
};

// Personas — BD-specific persona overlay on top of role-based access.
// `getMine` returns null persona for users that don't fall into a BD bucket.
export const personasAPI = {
  getMine: () => api.get('/personas/me').then(r => r.data),
  list: () => api.get('/personas').then(r => r.data),
  get: (key) => api.get(`/personas/${key}`).then(r => r.data),
};

export const supportAPI = {
  // Tickets
  listTickets: (params) => api.get('/support/tickets', { params }).then(r => r.data),
  getTicket: (id) => api.get(`/support/tickets/${id}`).then(r => r.data),
  createTicket: (data) => api.post('/support/tickets', data).then(r => r.data),
  // Upload a single attachment (screenshot, doc) BEFORE creating the ticket.
  // Returns {url, filename, size_bytes, mime_type}. Pass the urls in
  // createTicket(...).attachments to link them to the ticket.
  uploadTicketAttachment: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/support/attachments/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  updateTicket: (id, data) => api.patch(`/support/tickets/${id}`, data).then(r => r.data),
  addTicketComment: (id, data) => api.post(`/support/tickets/${id}/comments`, data).then(r => r.data),
  createKbFromTicket: (id, data = {}) => api.post(`/support/tickets/${id}/knowledge-base`, data).then(r => r.data),

  // Feature requests
  listFeatures: (params) => api.get('/support/feature-requests', { params }).then(r => r.data),
  getFeature: (id) => api.get(`/support/feature-requests/${id}`).then(r => r.data),
  createFeature: (data) => api.post('/support/feature-requests', data).then(r => r.data),
  updateFeature: (id, data) => api.patch(`/support/feature-requests/${id}`, data).then(r => r.data),
  addFeatureComment: (id, data) => api.post(`/support/feature-requests/${id}/comments`, data).then(r => r.data),
  createKbFromFeature: (id, data = {}) => api.post(`/support/feature-requests/${id}/knowledge-base`, data).then(r => r.data),

  // Knowledge base
  listKnowledge: (params) => api.get('/knowledge-base', { params }).then(r => r.data),
  getKnowledge: (id) => api.get(`/knowledge-base/${id}`).then(r => r.data),
  createKnowledge: (data) => api.post('/knowledge-base', data).then(r => r.data),
  updateKnowledge: (id, data) => api.patch(`/knowledge-base/${id}`, data).then(r => r.data),
  addKnowledgeFeedback: (id, data) => api.post(`/knowledge-base/${id}/feedback`, data).then(r => r.data),

  // Dashboard
  getDashboardCounts: () => api.get('/support/dashboard/counts').then(r => r.data),
};

// Attach the profitability + CRM funnel/ROI methods to the existing
// analyticsAPI export (don't redeclare — there's already one above).
analyticsAPI.projectProfitability = (params) =>
  api.get('/analytics/projects/profitability', { params }).then(r => r.data);
// The rows behind one figure on a project's page — the FL proof, reached by
// project id so the CS/BD who own the project can open it without finance
// access. Scoped server-side; a project outside the caller's scope is a 404.
analyticsAPI.projectFigureProof = (projectId, figure) =>
  api.get(`/analytics/projects/${projectId}/proof/${figure}`).then(r => r.data);
analyticsAPI.crmPipelineFunnel = (params) =>
  api.get('/analytics/crm/pipeline-funnel', { params }).then(r => r.data);
analyticsAPI.crmSourceRoi = (params) =>
  api.get('/analytics/crm/source-roi', { params }).then(r => r.data);

// Notifications — backs the header bell (Phase 0 workflow foundations).
export const notificationsAPI = {
  list: (params = {}) => api.get('/notifications', { params }).then((r) => r.data),
  unreadCount: () => api.get('/notifications/unread-count').then((r) => r.data),
  markRead: (ids) => api.post('/notifications/read', ids ? { ids } : {}).then((r) => r.data),
};

// Web Push (T8) — device subscription management. Delivery of the actual push
// is server-side (background dispatcher); this only registers/removes the
// browser's PushSubscription. Self-scoped per user.
export const pushAPI = {
  vapidPublicKey: () => api.get('/push/vapid-public-key').then((r) => r.data),
  subscribe: (subscription, userAgent) =>
    api.post('/push/subscribe', {
      endpoint: subscription.endpoint,
      keys: subscription.keys,
      user_agent: userAgent,
    }).then((r) => r.data),
  unsubscribe: (endpoint) => api.post('/push/unsubscribe', { endpoint }).then((r) => r.data),
};

// Generic context-based chat (Phase 1). One client serves every module — the
// (contextType, contextId) pair selects the conversation. Phase 1 wires
// contextType='project'.
export const chatAPI = {
  // Configurable client policy (edit / delete-for-everyone time windows).
  config: () => api.get('/chat/config').then((r) => r.data),
  // Sidebar: all of my conversations (groups + DMs), most recent first.
  myConversations: () => api.get('/chat/conversations').then((r) => r.data),
  pinConversation: (conversationId, pinned) =>
    api.post(`/chat/conversations/${conversationId}/pin`, { pinned }).then((r) => r.data),
  typingPing: (conversationId) =>
    api.post(`/chat/conversations/${conversationId}/typing`).then((r) => r.data),
  whoTyping: (conversationId) =>
    api.get(`/chat/conversations/${conversationId}/typing`).then((r) => r.data),
  conversationDetail: (conversationId) =>
    api.get(`/chat/conversations/${conversationId}/detail`).then((r) => r.data),
  // Group admin: manage membership.
  addableMembers: (conversationId, q = '') =>
    api.get(`/chat/conversations/${conversationId}/addable`, { params: { q } }).then((r) => r.data),
  addMembers: (conversationId, userIds) =>
    api.post(`/chat/conversations/${conversationId}/members`, { user_ids: userIds }).then((r) => r.data),
  removeMember: (conversationId, userId) =>
    api.delete(`/chat/conversations/${conversationId}/members/${userId}`).then((r) => r.data),
  // Per-record (project) helpers for the Chat launcher.
  contextGroups: (contextType, contextId) =>
    api.get(`/chat/context/${contextType}/${contextId}/groups`).then((r) => r.data),
  contextMembers: (contextType, contextId) =>
    api.get(`/chat/context/${contextType}/${contextId}/members`).then((r) => r.data),
  createGroup: (payload) => api.post('/chat/groups', payload).then((r) => r.data),
  // Self-service group + directory + full group management.
  createGroupNew: (payload) => api.post('/chat/groups/new', payload).then((r) => r.data),
  searchUsers: (q = '') => api.get('/chat/users', { params: { q } }).then((r) => r.data),
  // Combined search: people (for a DM) + my groups matching by name/member.
  searchAll: (q = '') => api.get('/chat/search', { params: { q } }).then((r) => r.data),
  updateGroup: (conversationId, patch) =>
    api.patch(`/chat/conversations/${conversationId}`, patch).then((r) => r.data),
  setMemberRole: (conversationId, userId, role) =>
    api.post(`/chat/conversations/${conversationId}/members/${userId}/role`, { role }).then((r) => r.data),
  transferOwnership: (conversationId, userId) =>
    api.post(`/chat/conversations/${conversationId}/transfer/${userId}`).then((r) => r.data),
  leaveGroup: (conversationId) =>
    api.post(`/chat/conversations/${conversationId}/leave`).then((r) => r.data),
  deleteGroup: (conversationId) =>
    api.delete(`/chat/conversations/${conversationId}`).then((r) => r.data),
  getConversation: (contextType, contextId) =>
    api.get(`/chat/context/${contextType}/${contextId}/conversation`).then((r) => r.data),
  openDm: (otherUserId) =>
    api.post(`/chat/dm/${otherUserId}`).then((r) => r.data),
  presencePing: () => api.post('/chat/presence/ping').then((r) => r.data),
  presence: (userIds) =>
    api.get('/chat/presence', { params: { user_ids: (userIds || []).join(',') } }).then((r) => r.data),
  listMessages: (conversationId, params = {}) =>
    api.get(`/chat/conversations/${conversationId}/messages`, { params }).then((r) => r.data),
  sendMessage: (conversationId, payload) =>
    api.post(`/chat/conversations/${conversationId}/messages`, payload).then((r) => r.data),
  markRead: (conversationId, messageId) =>
    api.post(`/chat/conversations/${conversationId}/read`, { message_id: messageId }).then((r) => r.data),
  toggleReaction: (conversationId, messageId, emoji) =>
    api.post(`/chat/conversations/${conversationId}/messages/${messageId}/reactions`, { emoji }).then((r) => r.data),
  editMessage: (conversationId, messageId, body) =>
    api.patch(`/chat/conversations/${conversationId}/messages/${messageId}`, { body }).then((r) => r.data),
  deleteMessage: (conversationId, messageId, forEveryone) =>
    api.post(`/chat/conversations/${conversationId}/messages/${messageId}/delete`, { for_everyone: forEveryone }).then((r) => r.data),
  messageInfo: (conversationId, messageId) =>
    api.get(`/chat/conversations/${conversationId}/messages/${messageId}/info`).then((r) => r.data),
  pinMessage: (conversationId, messageId, pinned) =>
    api.post(`/chat/conversations/${conversationId}/messages/${messageId}/pin`, { pinned }).then((r) => r.data),
  listPinned: (conversationId) =>
    api.get(`/chat/conversations/${conversationId}/pinned`).then((r) => r.data),
  forward: (messageIds, targetConversationIds) =>
    api.post('/chat/forward', { message_ids: messageIds, target_conversation_ids: targetConversationIds }).then((r) => r.data),
  unreadCounts: () => api.get('/chat/unread-counts').then((r) => r.data),
  upload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/chat/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },
  // Returns the file as a Blob from a forced-download response (server sets
  // Content-Disposition) so cross-origin GCS files download reliably.
  downloadAttachment: (attachmentId) =>
    api.get(`/chat/attachments/${attachmentId}/download`, { responseType: 'blob' }).then((r) => r.data),
};

// Meetings + Google Meet/Calendar — generic context-based, mirrors chatAPI.
export const meetingAPI = {
  list: (params = {}) => api.get('/meetings', { params }).then((r) => r.data),
  calendar: (params = {}) => api.get('/meetings/calendar', { params }).then((r) => r.data),
  googleEvents: (start, end) => api.get('/meetings/google/events', { params: { start, end } }).then((r) => r.data),
  get: (id) => api.get(`/meetings/${id}`).then((r) => r.data),
  create: (payload) => api.post('/meetings', payload).then((r) => r.data),
  update: (id, patch) => api.patch(`/meetings/${id}`, patch).then((r) => r.data),
  reschedule: (id, payload) => api.post(`/meetings/${id}/reschedule`, payload).then((r) => r.data),
  cancel: (id) => api.post(`/meetings/${id}/cancel`).then((r) => r.data),
  remove: (id) => api.delete(`/meetings/${id}`).then((r) => r.data),
  addParticipants: (id, body) => api.post(`/meetings/${id}/participants`, body).then((r) => r.data),
  removeParticipant: (id, participantId) =>
    api.delete(`/meetings/${id}/participants/${participantId}`).then((r) => r.data),
  contextMembers: (contextType, contextId) =>
    api.get(`/meetings/context/${contextType}/${contextId}/members`).then((r) => r.data),
  upload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/meetings/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },
  googleStatus: () => api.get('/meetings/google/status').then((r) => r.data),
  googleConnectUrl: () => api.get('/meetings/google/connect').then((r) => r.data),
  googleDisconnect: () => api.post('/meetings/google/disconnect').then((r) => r.data),
  prefs: () => api.get('/meetings/calendar/preferences').then((r) => r.data),
  updatePrefs: (patch) => api.put('/meetings/calendar/preferences', patch).then((r) => r.data),
  availability: (userIds, start, end) =>
    api.post('/meetings/availability', { user_ids: userIds, start, end }).then((r) => r.data),
  blocks: (start, end) => api.get('/meetings/calendar/blocks', { params: { start, end } }).then((r) => r.data),
  createBlock: (payload) => api.post('/meetings/calendar/blocks', payload).then((r) => r.data),
  deleteBlock: (id) => api.delete(`/meetings/calendar/blocks/${id}`).then((r) => r.data),
  quickStatus: (kind, timezone) => api.post('/meetings/calendar/blocks/quick', { kind, timezone }).then((r) => r.data),
};

// Workflow / TAT engine — read a case's process timeline + the user's open work.
export const workflowAPI = {
  caseSteps: (caseType, caseId) =>
    api.get('/workflow/steps', { params: { case_type: caseType, case_id: caseId } }).then((r) => r.data),
  caseStepsBulk: (caseType, caseIds) =>
    api.get('/workflow/case-steps', { params: { case_type: caseType, case_ids: (caseIds || []).join(',') } }).then((r) => r.data),
  lineage: (caseType, caseId) =>
    api.get('/workflow/lineage', { params: { case_type: caseType, case_id: caseId } }).then((r) => r.data),
  myWork: () => api.get('/workflow/my-work').then((r) => r.data),
  myApprovedPayments: () => api.get('/workflow/my-approved-payments').then((r) => r.data),
  // Full payment approval history for the logged-in approver — both payment
  // steps (PO + driver) and both outcomes (released/approved and rejected).
  myPaymentApprovals: () => api.get('/workflow/my-payment-approvals').then((r) => r.data),
  bottlenecks: () => api.get('/workflow/analytics/bottlenecks').then((r) => r.data),
  reassign: (stepId, assigneeUserId) =>
    api.post(`/workflow/steps/${stepId}/reassign`, { assignee_user_id: assigneeUserId }).then((r) => r.data),
  act: (stepId, action, note) =>
    api.post(`/workflow/steps/${stepId}/act`, { action, note }).then((r) => r.data),
  bulkAct: (stepIds, intent, note) =>
    api.post('/workflow/steps/bulk-act', { step_ids: stepIds, intent, note }).then((r) => r.data),
  listHolidays: () => api.get('/workflow/holidays').then((r) => r.data),
  addHoliday: (data) => api.post('/workflow/holidays', data).then((r) => r.data),
  deleteHoliday: (id) => api.delete(`/workflow/holidays/${id}`),
};

// Gamification — personal performance + leaderboard (My Account > Performance).
export const gamificationAPI = {
  me: () => api.get('/gamification/me').then((r) => r.data),
  leaderboard: (limit = 10) => api.get('/gamification/leaderboard', { params: { limit } }).then((r) => r.data),
  award: (data) => api.post('/gamification/award', data).then((r) => r.data),
  attendance: (days = 30) => api.get('/gamification/attendance', { params: { days } }).then((r) => r.data),
};
